import { createDeferred, createEffect, createMemo, createResource, ErrorBoundary, For, onCleanup, onMount, Show, Suspense, untrack, type JSX } from "solid-js"
import { createStore } from "solid-js/store"
import { A, useNavigate, useSearchParams } from "@solidjs/router"
import {
  datoxAgentMode,
  getFile,
  getArtifact,
  getCurrentUser,
  listProjects,
  uploadFile,
  getFileContentBlob,
  getSessionContextByRemote,
  isDashboardShareArtifact,
  downloadArtifact,
  exportArtifact,
  deleteArtifact,
  deleteFile,
  listArtifactsForPeriod,
  listDashboards,
  listApprovals,
  listExtractions,
  listFiles,
  listTransformations,
  requestApprovals,
  updateApprovalReviewer,
  deleteApprovalReviewer,
  type DatoxArtifact,
  type DatoxApproval,
  type DatoxExtraction,
  type DatoxFile,
  type DatoxTransformation,
} from "@/datox/api"
import { useLanguage } from "@/context/language"
import { Persist, persisted } from "@/utils/persist"
import { FileIcon } from "@opencode-ai/ui/file-icon"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import { TooltipV2 } from "@opencode-ai/ui/v2/tooltip-v2"
import { MenuV2 } from "@opencode-ai/ui/v2/menu-v2"
import { DatoxDashboardBuilder } from "./datox-dashboard-builder"
import { DatoxReportPreviewLink } from "@/components/datox-report-preview-link"
import { DatoxReportDeleteButton } from "@/components/datox-report-delete-button"
import { DatoxContentLoading } from "@/components/datox-content-loading"
import { DatoxCategoriesTab } from "@/components/datox-categories-tab"
import { workflowCategories } from "@/datox/workspace-categories"
import type { FactsheetWorkspace } from "@/datox/workspaces"
import { getDataCategories } from "@/datox/automations"
import { DatoxWorkflowRunAction } from "@/components/datox-workflow-run-action"
import { DatoxReportRunAction } from "@/components/datox-report-run-action"
import { reportWorkflowFiles } from "@/datox/report-workflow"
import { DatoxRevisionSelect } from "@/components/datox-revision-select"
import { DatoxWorkflowStatus } from "@/components/datox-workflow-status"
import { DatoxFilesIntegrationsAction } from "@/components/datox-files-integrations-dialog"
import { fileSyncFailed, useFileIntegrations } from "@/datox/file-integrations"
import { showToast } from "@/utils/toast"
import { workflowResultTabs, workflowTabCompletion, type WorkflowResultTab } from "@/datox/workflow-tab-status"
import "@/datox/report-workspace.css"
import "@/datox/workflow-tabs.css"
import "@/datox/v2/workflow-design.css"
import "@/datox/approval.css"
import { approvalRecipients } from "@/datox/approval-recipients"
import { clientApprovalArtifacts, type ReportApprovalScope } from "@/datox/report-approvals"
import { resolveSessionReport, resolveWorkflowReports, sessionReportPath } from "@/datox/session-report"
import { declaredExtractionTableCount, tablePreviewPage, type TablePreviewSource } from "@/datox/table-preview"
import { htmlReportFormat, reportDisplayTitle, reportPreviewPath } from "@/datox/report-preview"

type DatoxFilesPanelProps = { sessionID?: string; projectID?: string; periodID?: string; periodName?: string; caseID?: string; itemID?: string; initialTab?: PipelineTab; initialTabKey?: number; mode?: "workflow" | "dashboard" | "reports"; workspace?: FactsheetWorkspace; approvalScope?: ReportApprovalScope; categoryReadOnly?: boolean; onWorkspaceSaved?: (workspace: FactsheetWorkspace) => void; onWorkspaceRefresh?: () => unknown }

export function DatoxFilesPanel(props: DatoxFilesPanelProps) {
  // Retain cached results on refresh, but never carry them into another scope.
  return <Show keyed when={JSON.stringify([props.sessionID, props.projectID, props.periodID, props.caseID])}>
    {(_scope) => <DatoxFilesPanelContent {...props} />}
  </Show>
}

function DatoxFilesPanelContent(props: DatoxFilesPanelProps) {
  const language = useLanguage()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const [store, setStore] = createStore({ filter: "", activeFileID: "", reportVisited: props.mode === "reports", reportRefresh: 0, shareArtifactID: "", uploading: false, runError: "", activeTabs: [] as WorkflowResultTab[] })
  const [tabs, setTabs, , tabsReady] = persisted(
    Persist.global(`datox-workflow-tab:${props.sessionID ?? `${props.projectID}:${props.periodID}`}`),
    createStore({ tab: "files" as PipelineTab }),
  )
  const [sessionContext] = createResource(() => props.sessionID, getSessionContextByRemote)
  const [linkedReport] = createResource(() => !props.workspace && !props.approvalScope ? sessionContext.latest : undefined,
    (value) => resolveSessionReport(value).catch(() => undefined))
  const linkedReportPath = () => {
    const report = linkedReport.latest
    const session = sessionContext.latest
    if (!report || !session || linkedReport.error) return
    const scope = context()
    if (!scope || scope.project_id !== session.project_id || (scope.period_id ?? null) !== (session.period_id ?? null)) return
    return sessionReportPath(session.project_id, report)
  }
  const projectID = createMemo(() => props.projectID)
  const periodID = createMemo(() => props.periodID)
  const caseID = createMemo(() => props.caseID)
  const periodName = createMemo(() => props.periodName)
  const context = createMemo(() => {
    if (projectID()) return { project_id: projectID()!, period_id: periodID(), case_id: caseID(), session_metadata: { period: { name: periodName() } } }
    const value = sessionContext.latest
    if (!value) return undefined
    const sharedCaseID = value.workflow_case?.shared_workflow_records && typeof value.workflow_case.case_id === "string" ? value.workflow_case.case_id : undefined
    return { ...value, case_id: sharedCaseID }
  })
  const periodReportScope = createMemo(() => {
    const value = context()
    if (props.sessionID || props.workspace || props.approvalScope || props.mode === "reports" || props.mode === "dashboard" || !value?.period_id) return
    return { projectID: value.project_id, periodID: value.period_id, caseID: value.case_id }
  })
  const [periodReports, periodReportActions] = createResource(periodReportScope, (scope) => resolveWorkflowReports(scope).catch(() => ({ direct: false, reports: [] })))
  createEffect(() => {
    if (!periodReportScope()) return
    const refresh = () => { void Promise.resolve(periodReportActions.refetch()).catch(() => undefined) }
    window.addEventListener("focus", refresh)
    onCleanup(() => window.removeEventListener("focus", refresh))
  })
  const [files, filesActions] = createResource(context, (value) => {
    if (!value) return []
    return listFiles(value.project_id, value.period_id ?? undefined, value.case_id)
  })
  const [fileAccess, fileAccessActions] = createResource(() => props.mode !== "reports" && props.mode !== "dashboard" ? context()?.project_id : undefined, async (projectID) => {
    const [user, projects] = await Promise.all([getCurrentUser(), listProjects()])
    return user.company_role !== "viewer" && projects.some((project) => project.id === projectID && ["owner", "editor"].includes(project.project_role ?? ""))
  })
  const integrationScope = createMemo(() => {
    const value = context()
    if (!value?.period_id || value.case_id || !fileAccess.latest || fileAccess.loading || fileAccess.error) return undefined
    const metadata = value.session_metadata?.period as { name?: string } | undefined
    return { projectID: value.project_id, periodID: value.period_id, periodName: metadata?.name ?? value.period_id }
  })
  const integrations = useFileIntegrations({
    scope: integrationScope,
    onFiles: () => {
      const selected = activeFile()?.id
      if (selected) setStore("activeFileID", selected)
      void Promise.resolve(filesActions.refetch()).catch(() => undefined)
    },
    onComplete: (job, scope) => showToast({
      title: language.t("datox.fileIntegrations.title"),
      description: fileSyncFailed(job) ? language.t("datox.fileIntegrations.needsAttention")
        : !job.matched ? language.t("datox.fileIntegrations.noFiles", { period: scope.periodName })
        : !job.imported ? language.t("datox.fileIntegrations.alreadyPresent")
        : language.t("datox.fileIntegrations.completed", { imported: job.imported, period: scope.periodName }),
    }),
  })
  const [extractions, extractionActions] = createResource(context, (value) => {
    if (!value) return []
    return listExtractions(value.project_id, value.period_id, value.case_id)
  })
  const [categories, categoryActions] = createResource(() => props.mode !== "reports" && props.mode !== "dashboard" ? context() : undefined,
    (value) => getDataCategories(value.project_id, value.period_id ?? undefined, value.case_id))
  const categoryRows = createMemo(() => workflowCategories(props.workspace?.definition.categories ?? [], categories.error ? [] : categories.latest ?? []))
  const [transformations, transformationActions] = createResource(context, (value) => {
    if (!value) return []
    return listTransformations(value.project_id, value.period_id, value.case_id)
  })
  const reportContext = createMemo(() => {
    const value = context()
    if (!value || (props.mode === "dashboard" && !store.reportVisited)) return undefined
    return { projectID: value.project_id, periodID: value.period_id, caseID: value.case_id, refresh: store.reportRefresh }
  })
  const [artifacts, artifactActions] = createResource(reportContext, (value) => {
    if (!value) return []
    value.refresh
    return listArtifactsForPeriod(value.projectID, value.periodID, "report", value.caseID).then((artifacts) => artifacts.filter((artifact) => !isDashboardShareArtifact(artifact)))
  })
  const [templates, templateActions] = createResource(() => store.reportVisited ? reportContext() : undefined, (value) => {
    if (!value) return []
    value.refresh
    return listArtifactsForPeriod(value.projectID, undefined, "report_template")
  })
  const [approvals, approvalActions] = createResource(context, (value) => {
    if (!value) return []
    return listApprovals(value.project_id, value.period_id, value.case_id)
  })
  const [dashboards, dashboardActions] = createResource(() => props.mode !== "reports" && props.mode !== "dashboard" ? context() : undefined,
    (value) => listDashboards(value.project_id, value.period_id, value.case_id))
  const approvalArtifacts = createMemo(() => props.approvalScope ? clientApprovalArtifacts(artifacts.latest ?? [], props.approvalScope) : artifacts.latest ?? [])
  const approvalArtifactIDs = createMemo(() => new Set(approvalArtifacts().map((artifact) => artifact.id)))
  const clientApprovals = createMemo(() => (approvals.latest ?? []).filter((approval) => approvalArtifactIDs().has(approval.report_artifact_id)))
  const approvalKey = createMemo(() => JSON.stringify([props.approvalScope?.workspaceID, props.approvalScope?.targetID, props.approvalScope?.dashboardID]))
  const completed = createMemo(() => workflowTabCompletion({
    scope: { projectID: context()?.project_id ?? "", periodID: context()?.period_id ?? undefined, caseID: context()?.case_id },
    files: files.error ? undefined : files.latest,
    categories: categoryRows(),
    extractions: extractions.error ? undefined : extractions.latest,
    transformations: transformations.error ? undefined : transformations.latest,
    artifacts: artifacts.error ? undefined : artifacts.latest,
    dashboards: dashboards.error ? undefined : dashboards.latest,
    approvals: approvals.error ? undefined : clientApprovals(),
  }))
  const filteredFiles = createMemo(() => {
    const query = store.filter.trim().toLowerCase()
    const values = files.error ? [] : files.latest ?? []
    if (!query) return values
    return values.filter((file) => file.filename.toLowerCase().includes(query))
  })
  const activeFile = createMemo(() => filteredFiles().find((file) => file.id === store.activeFileID) ?? filteredFiles()[0])
  const fileToLoad = createMemo(() => props.mode !== "reports" && props.mode !== "dashboard" && tabs.tab === "files" ? activeFile() : undefined, undefined, {
    equals: (previous, next) => previous?.project_id === next?.project_id && previous?.id === next?.id &&
      previous?.updated_at === next?.updated_at && previous?.status === next?.status,
  })
  const [detail, fileDetailActions] = createResource(fileToLoad, (file) => getFile(file.project_id, file.id))
  const activeDetail = createMemo(() => {
    const file = activeFile()
    const value = detail.error ? undefined : detail.latest
    return file && value?.id === file.id ? value : undefined
  })
  const period = createMemo(() => {
    const metadata = context()?.session_metadata?.period
    if (!metadata || typeof metadata !== "object") return ""
    const periodRecord = metadata as { name?: unknown }
    return typeof periodRecord.name === "string" ? periodRecord.name : ""
  })

  const resultActions = { files: filesActions.refetch, categories: categoryActions.refetch, extractions: extractionActions.refetch,
    transformations: transformationActions.refetch, output: transformationActions.refetch, report: artifactActions.refetch,
    dashboard: dashboardActions.refetch, approval: approvalActions.refetch }
  const pendingResults = new Set<WorkflowResultTab>()
  let resultTimer: ReturnType<typeof setTimeout> | undefined
  let refreshingResults = false
  let disposed = false
  const refreshSavedResults = (results: readonly WorkflowResultTab[]) => {
    results.forEach((tab) => pendingResults.add(tab))
    if (resultTimer || refreshingResults || document.hidden || disposed) return
    resultTimer = setTimeout(async () => {
      resultTimer = undefined
      refreshingResults = true
      // Coalesce parallel saves and drain another batch if a save arrives during a request.
      while (pendingResults.size && !disposed && !document.hidden) {
        const refreshers = [...new Set([...pendingResults].map((tab) => resultActions[tab]))]
        pendingResults.clear()
        await Promise.allSettled(refreshers.map((refresh) => Promise.resolve().then(() => refresh())))
      }
      refreshingResults = false
    }, 250)
  }
  createEffect(() => {
    context()
    if (resultTimer) clearTimeout(resultTimer)
    resultTimer = undefined
    pendingResults.clear()
  })
  onCleanup(() => {
    disposed = true
    if (resultTimer) clearTimeout(resultTimer)
    pendingResults.clear()
  })
  const refetchWorkflow = () => refreshSavedResults(workflowResultTabs)

  const selectTab = (tab: PipelineTab) => {
    if (tab === "dashboard") tab = "report"
    if (tab === "report" || tab === "approval" || tab === "output") setStore("reportVisited", true)
    // Persistence reads the store; do not subscribe navigation effects to tab changes.
    untrack(() => setTabs("tab", tab))
  }
  createEffect(() => {
    props.initialTabKey
    if (tabsReady() && props.initialTab) selectTab(props.initialTab)
  })
  createEffect(() => {
    if (tabsReady() && tabs.tab === "dashboard") selectTab("report")
  })

  const shareReport = (artifact: DatoxArtifact) => {
    setStore("shareArtifactID", artifact.id)
    selectTab("approval")
    if (props.mode !== "reports") return
    const query = new URLSearchParams({ tab: "approval", report_id: artifact.id })
    const caseID = artifact.case_id ?? context()?.case_id
    if (caseID) query.set("case_id", caseID)
    navigate(`/catalog/workflows/${encodeURIComponent(artifact.project_id)}/${encodeURIComponent(artifact.period_id ?? context()?.period_id ?? "project")}?${query}`)
  }

  createEffect(() => {
    if (props.mode === "reports" || props.mode === "dashboard" || search.tab !== "approval") return
    setStore("shareArtifactID", typeof search.report_id === "string" ? search.report_id : "")
    selectTab("approval")
  })

  createEffect(() => {
    if (tabs.tab === "report" || tabs.tab === "approval" || tabs.tab === "output") setStore("reportVisited", true)
  })

  createEffect(() => {
    const handler = () => selectTab("approval")
    window.addEventListener("datox:open-share-access", handler)
    onCleanup(() => window.removeEventListener("datox:open-share-access", handler))
  })

  createEffect(() => {
    if (props.mode === "reports") setStore("reportVisited", true)
  })

  const removeFile = async (file: DatoxFile) => {
    if (fileAccess.loading || fileAccess.error || !fileAccess.latest) return
    if (activeFile()?.id === file.id) setStore("activeFileID", "")
    await deleteFile(file.project_id, file.id)
    await filesActions.refetch()
    const next = filteredFiles().find((item) => item.id !== file.id)
    setStore("activeFileID", next?.id ?? "")
  }

  const uploadFiles = async (uploads: File[]) => {
    const scope = context()
    if (!scope?.period_id || store.uploading || fileAccess.loading || fileAccess.error || !fileAccess.latest) return
    setStore({ uploading: true, runError: "" })
    let lastID = ""
    try {
      for (const file of uploads) {
        const uploaded = await uploadFile(scope.project_id, file, scope.period_id, scope.case_id, props.sessionID ? sessionContext()?.session_id : undefined)
        lastID = uploaded.id
      }
    } finally {
      try {
        await filesActions.refetch()
        if (lastID) setStore({ activeFileID: lastID, filter: "" })
      } finally { setStore("uploading", false) }
    }
  }

  onMount(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ sessionID?: string }>).detail
      if (detail?.sessionID !== props.sessionID) return
      refetchWorkflow()
    }
    const focusHandler = () => { if (!document.hidden) refetchWorkflow() }
    window.addEventListener("datox-files-uploaded", handler)
    window.addEventListener("focus", focusHandler)
    document.addEventListener("visibilitychange", focusHandler)
    onCleanup(() => {
      window.removeEventListener("datox-files-uploaded", handler)
      window.removeEventListener("focus", focusHandler)
      document.removeEventListener("visibilitychange", focusHandler)
    })
  })

  return (
    <div data-workflow-panel data-lesha-workflow={datoxAgentMode() || undefined} class="flex h-full min-h-0 flex-col bg-v2-background-bg-base text-v2-text-text-base">
      <Show when={props.mode === "reports"}>
        <Suspense fallback={<DatoxContentLoading title={language.t("datox.pipeline.report.opening")} detail={language.t("datox.pipeline.report.loadingHint")} />}>
        <ReportTab
          initialArtifactID={props.itemID}
          onShare={shareReport}
          artifacts={artifacts.error ? [] : artifacts.latest ?? []}
          templates={templates.error ? [] : templates.latest ?? []}
          loading={artifacts.loading || templates.loading || sessionContext.loading}
          error={!!artifacts.error || !!templates.error}
          onRefresh={() => {
            setStore("reportVisited", true)
            setStore("reportRefresh", store.reportRefresh + 1)
          }}
          onDelete={async (artifact) => {
            await deleteArtifact(artifact.project_id, artifact.id)
            await artifactActions.refetch()
            await templateActions.refetch()
          }}
        />
        </Suspense>
      </Show>
      <Show when={props.mode === "dashboard"}>
        <Show when={context()} fallback={<div class="flex flex-1 items-center justify-center text-13-regular text-v2-text-text-muted">{language.t("datox.dashboard.loading")}</div>}>
          {(value) => <DatoxDashboardBuilder projectID={value().project_id} periodID={value().period_id} caseID={value().case_id} dashboardID={props.itemID} transformations={transformations.latest ?? []} />}
        </Show>
      </Show>
      <Show when={props.mode !== "reports" && props.mode !== "dashboard"}>
        <Show when={context()}>{(scope) => <div class="workflow-context-actions"><DatoxWorkflowStatus projectID={scope().project_id} periodID={scope().period_id ?? undefined} caseID={scope().case_id} onResultsSaved={refreshSavedResults} onActivity={(tabs) => setStore("activeTabs", tabs)} /><Show when={linkedReportPath()}>{(href) => <A class="workflow-action" href={href()} title={linkedReport.latest?.name}><IconV2 name="report" size="small" />{language.t("datox.report.openReport")}</A>}</Show>
          <Show when={periodReportScope()}>
            <Show when={periodReports.latest?.direct ? periodReports.latest.reports[0] : undefined} fallback={
              <MenuV2 placement="bottom-end"><MenuV2.Trigger class="workflow-action" disabled={periodReports.loading || !periodReports.latest?.reports.length}>
                <IconV2 name="report" size="small" />{language.t("datox.report.openReport")}<IconV2 name="chevron-down" size="small" />
              </MenuV2.Trigger><MenuV2.Portal><MenuV2.Content><For each={periodReports.latest?.reports ?? []}>{(report) =>
                <MenuV2.Item onSelect={() => navigate(report.href)}>{report.name}</MenuV2.Item>
              }</For></MenuV2.Content></MenuV2.Portal></MenuV2>
            }>{(report) => <A class="workflow-action" href={report().href} title={report().name}><IconV2 name="report" size="small" />{language.t("datox.report.openReport")}</A>}</Show>
          </Show><Show when={props.workspace} fallback={<DatoxWorkflowRunAction projectID={scope().project_id} periodID={scope().period_id ?? undefined} caseID={scope().case_id}
          disabled={store.uploading || integrations.blocked() || files.loading || !!files.error || fileAccess.loading || !!fileAccess.error || !fileAccess.latest}
          hasFiles={!files.error && Boolean(files.latest?.some((f) => f.period_id === scope().period_id && (f.case_id ?? undefined) === scope().case_id))}
          hasSourceOutputs={!transformations.error && Boolean(transformations.latest?.some((t) => t.period_id === scope().period_id && (t.case_id ?? undefined) === scope().case_id && t.status === "completed"))}
           uploading={store.uploading || integrations.blocked()} filesLoading={files.loading} onError={(message) => setStore("runError", message)} />}>
           <DatoxReportRunAction workspace={props.workspace!} fileIDs={reportWorkflowFiles(props.workspace!, files.error ? [] : files.latest ?? [])}
             disabled={!!props.categoryReadOnly || !props.onWorkspaceSaved || store.uploading || integrations.blocked() || files.loading || !!files.error || fileAccess.loading || !!fileAccess.error || !fileAccess.latest}
             onSaved={(workspace) => props.onWorkspaceSaved?.(workspace)} onRefresh={() => props.onWorkspaceRefresh?.()} onError={(message) => setStore("runError", message)} />
        </Show></div>}</Show>
      <nav class="workflow-tabs" aria-label={language.t("datox.pipeline.workspace.stages")}>
        <PipelineTabButton active={tabs.tab === "files"} completed={completed().files} working={store.uploading || store.activeTabs.includes("files")} count={files.error ? undefined : files.latest?.length} label={language.t("datox.pipeline.files")} onClick={() => selectTab("files")} />
        <PipelineTabButton active={tabs.tab === "categories"} completed={completed().categories} working={store.activeTabs.includes("categories")} count={categoryRows().length} label={language.t("datox.categories.title")} onClick={() => selectTab("categories")} />
        <PipelineTabButton active={tabs.tab === "extractions"} completed={completed().extractions} working={store.activeTabs.includes("extractions")} count={extractions.error ? undefined : extractions.latest?.length} label={language.t("datox.pipeline.extractions")} onClick={() => selectTab("extractions")} />
        <PipelineTabButton active={tabs.tab === "transformations"} completed={completed().transformations} working={store.activeTabs.includes("transformations")} count={transformations.error ? undefined : transformations.latest?.length} label={language.t("datox.pipeline.transformations")} onClick={() => selectTab("transformations")} />
        <PipelineTabButton active={tabs.tab === "output"} completed={completed().output} working={store.activeTabs.includes("output")} label={language.t("datox.pipeline.output")} onClick={() => selectTab("output")} />
        <PipelineTabButton active={tabs.tab === "report"} completed={completed().report} working={store.activeTabs.includes("report")} count={artifacts.error ? undefined : artifacts.latest?.length} label={language.t("datox.pipeline.report")} onClick={() => selectTab("report")} />
        <PipelineTabButton active={tabs.tab === "approval"} completed={completed().approval} approved count={approvals.error ? undefined : clientApprovals().length} label={language.t("datox.pipeline.approval")} onClick={() => selectTab("approval")} />
      </nav>
      <Show when={store.runError}><p role="alert" class="workflow-error">{store.runError}</p></Show>
      <Show when={integrations.blocked()}><p role="status" class="workflow-notice">{language.t(integrations.active() ? "datox.fileIntegrations.inProgress" : "datox.fileIntegrations.needsAttention")}</p></Show>
      <div class="workflow-tab-content"><ErrorBoundary fallback={(_error, reset) => <div class="workflow-empty" role="alert"><h3>{language.t("datox.pipeline.workspace.loadError")}</h3><button type="button" class="workflow-action" onClick={async () => { await Promise.allSettled([filesActions.refetch(), extractionActions.refetch(), categoryActions.refetch(), transformationActions.refetch(), approvalActions.refetch(), artifactActions.refetch(), templateActions.refetch()]); reset() }}>{language.t("datox.pipeline.workspace.refresh")}</button></div>}><Suspense fallback={<DatoxContentLoading />}>
      <Show when={tabs.tab === "files"}>
        <FilesTab
          period={period()}
          filter={store.filter}
          setFilter={(value) => setStore("filter", value)}
          files={filteredFiles()}
          activeFileID={activeFile()?.id}
          selectFile={(fileID) => setStore("activeFileID", fileID)}
          detail={activeDetail()}
          loading={detail.loading && !activeDetail()}
          filesLoading={files.loading && !files.error && !files.latest}
          error={!!files.error || !!detail.error || !!fileAccess.error}
          canWrite={!fileAccess.loading && !fileAccess.error && !!fileAccess.latest && Boolean(context()?.period_id)}
          hasContext={!!context()}
          onDelete={removeFile}
          onUpload={uploadFiles}
          integrationsAction={<Show when={!context()?.case_id && props.mode !== "reports" && props.mode !== "dashboard"}><DatoxFilesIntegrationsAction scope={integrationScope()} disabled={store.uploading || !integrationScope()} controller={integrations} /></Show>}
          onRefresh={() => { refetchWorkflow(); void Promise.resolve(fileDetailActions.refetch()).catch(() => undefined); void Promise.resolve(fileAccessActions.refetch()).catch(() => undefined) }}
        />
      </Show>
      <Show when={tabs.tab === "extractions"}>
        <Suspense fallback={<DatoxContentLoading title={language.t("datox.pipeline.extraction.loading")} detail={language.t("datox.pipeline.extraction.loadingHint")} />}><DeferredExtractionView render={() => <RecordsTab title={language.t("datox.pipeline.extractions")} records={extractions.latest ?? []} kind="extraction" files={files.latest ?? []} refreshing={extractions.loading} onPrevious={() => selectTab("files")} />} /></Suspense>
      </Show>
      <Show when={tabs.tab === "categories" && context()}><Suspense fallback={<DatoxContentLoading />}><DatoxCategoriesTab categories={categories} rows={categoryRows()} workspace={props.workspace} inputFiles={files.error ? [] : files.latest ?? []} inputsBusy={store.uploading || integrations.blocked() || files.loading || !!files.error} readOnly={props.categoryReadOnly || !fileAccess.latest || fileAccess.loading || !!fileAccess.error} onSaved={props.onWorkspaceSaved} onRefresh={() => Promise.all([categoryActions.refetch(), props.onWorkspaceRefresh?.()])} onFiles={() => selectTab("files")} /></Suspense></Show>
      <Show when={tabs.tab === "transformations"}>
        <RecordsTab title={language.t("datox.pipeline.transformations")} records={transformations.latest ?? []} kind="transformation" onPrevious={() => selectTab("extractions")} />
      </Show>
      <Show when={tabs.tab === "output"}>
        <OutputTab transformations={transformations.latest ?? []} artifacts={artifacts.latest ?? []} onTransformations={() => selectTab("transformations")} />
      </Show>
      <Show when={tabs.tab === "report"}>
        <Suspense fallback={<DatoxContentLoading title={language.t("datox.pipeline.report.opening")} detail={language.t("datox.pipeline.report.loadingHint")} />}>
        <ReportTab
          onShare={shareReport}
          artifacts={artifacts.error ? [] : artifacts.latest ?? []}
          templates={templates.error ? [] : templates.latest ?? []}
          loading={artifacts.loading || templates.loading || sessionContext.loading}
          error={!!artifacts.error || !!templates.error}
          onRefresh={() => {
            setStore("reportVisited", true)
            setStore("reportRefresh", store.reportRefresh + 1)
          }}
          onDelete={async (artifact) => {
            await deleteArtifact(artifact.project_id, artifact.id)
            await artifactActions.refetch()
            await templateActions.refetch()
          }}
        />
        </Suspense>
      </Show>
      <Show when={tabs.tab === "approval"}>
        <Show keyed when={approvalKey()}>{(_selection) => <ApprovalTab
          initialArtifactID={approvalArtifactIDs().has(store.shareArtifactID) ? store.shareArtifactID : undefined}
          onReports={() => selectTab("report")}
          approvals={clientApprovals()}
          artifacts={approvalArtifacts()}
          onRequest={async (artifactID, emails, message) => {
            const value = context()
            if (!value) return
            if (!approvalArtifactIDs().has(artifactID)) throw new Error(language.t("datox.report.shareError"))
            await requestApprovals(value.project_id, artifactID, emails, message)
            await approvalActions.refetch()
          }}
          onUpdate={async (approvalID, email) => {
            const value = context()
            if (!value) return
            if (!clientApprovals().some((approval) => approval.id === approvalID)) throw new Error(language.t("datox.report.shareError"))
            await updateApprovalReviewer(value.project_id, approvalID, email)
            await approvalActions.refetch()
          }}
          onDelete={async (approvalID) => {
            const value = context()
            if (!value) return
            if (!clientApprovals().some((approval) => approval.id === approvalID)) throw new Error(language.t("datox.report.shareError"))
            await deleteApprovalReviewer(value.project_id, approvalID)
            await approvalActions.refetch()
          }}
        />}</Show>
      </Show>
      </Suspense></ErrorBoundary></div>
      </Show>
    </div>
  )
}

type PipelineTab = "files" | "categories" | "extractions" | "transformations" | "output" | "report" | "dashboard" | "approval"

function FilesTab(props: {
  period: string
  filter: string
  setFilter: (value: string) => void
  files: DatoxFile[]
  activeFileID?: string
  selectFile: (fileID: string) => void
  detail: Awaited<ReturnType<typeof getFile>> | undefined
  loading: boolean
  filesLoading: boolean
  error: boolean
  canWrite: boolean
  hasContext: boolean
  onDelete: (file: DatoxFile) => Promise<void>
  onUpload: (files: File[]) => Promise<void>
  onRefresh: () => void
  integrationsAction: JSX.Element
}) {
  const language = useLanguage()
  let uploadInput: HTMLInputElement | undefined
  const [state, setState] = createStore({ action: "", error: false, dragging: false })
  const perform = async (action: string, run: () => Promise<void>) => {
    if (state.action) return
    setState({ action, error: false })
    try { await run() } catch { setState("error", true) } finally { setState("action", "") }
  }
  const upload = (files: File[]) => {
    if (!props.canWrite || !files.length) return
    void perform("upload", () => props.onUpload(files))
  }
  return (
    <div class="workflow-files workflow-split" data-dragging={state.dragging} onDragOver={(event) => { event.preventDefault(); if (props.canWrite && !state.action) setState("dragging", true) }} onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setState("dragging", false) }} onDrop={(event) => { event.preventDefault(); setState("dragging", false); upload([...(event.dataTransfer?.files ?? [])]) }}>
      <input ref={uploadInput} type="file" multiple hidden disabled={!props.canWrite || !!state.action} aria-label={language.t("datox.pipeline.workspace.upload")} onChange={(event) => { const files = [...(event.currentTarget.files ?? [])]; event.currentTarget.value = ""; upload(files) }} />
      <aside class="workflow-sidebar">
        <div class="flex min-h-12 items-center justify-between gap-2 px-4">
          <div class="min-w-0">
            <p class="text-14-medium text-v2-text-text-base">{language.t("datox.files.panel.title")}</p>
            <Show when={props.period}>
              <p class="truncate text-12-regular text-v2-text-text-muted">{props.period}</p>
            </Show>
          </div>
        </div>
        <div class="workflow-source-actions"><Show when={props.canWrite}><button type="button" class="workflow-action" data-primary disabled={!!state.action} aria-busy={state.action === "upload"} onClick={() => uploadInput?.click()}>{language.t(state.action === "upload" ? "datox.pipeline.workspace.uploading" : "datox.pipeline.workspace.upload")}</button></Show>{props.integrationsAction}<button type="button" class="workflow-action" disabled={props.filesLoading || !!state.action} onClick={props.onRefresh}>{language.t("datox.pipeline.workspace.refresh")}</button></div>
        <div class="px-3 pb-2">
          <input
            class="h-8 w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-13-regular outline-none"
             placeholder={language.t("datox.files.panel.filter")}
             aria-label={language.t("datox.files.panel.filter")}
            value={props.filter}
            onInput={(event) => props.setFilter(event.currentTarget.value)}
          />
        </div>
        <div class="min-h-0 flex-1 overflow-auto px-2 pb-2">
          <Show when={!props.filesLoading} fallback={<DatoxContentLoading />}><For each={props.files} fallback={<Show when={props.filter.trim()} fallback={<EmptyList hasContext={props.hasContext} />}><p class="workflow-help">{language.t("datox.pipeline.workspace.noFilesMatch")}</p><button type="button" class="workflow-action" onClick={() => props.setFilter("")}>{language.t("datox.listing.clear")}</button></Show>}>
            {(file) => <FileRow file={file} active={props.activeFileID === file.id} onClick={() => props.selectFile(file.id)} />}
          </For></Show>
        </div>
      </aside>
      <main class="workflow-inspector">
        <Show when={state.error || props.error}><p role="alert" class="workflow-error">{language.t("datox.pipeline.workspace.fileError")}</p></Show>
        <Show when={state.action === "upload"}><p role="status" class="workflow-notice">{language.t("datox.pipeline.workspace.uploading")}</p></Show>
        <Show
          keyed
          when={props.detail?.id}
          fallback={props.loading || props.filesLoading ? <DatoxContentLoading title={language.t("datox.files.panel.loadingPreview")} /> : <div class="workflow-empty"><IconV2 name="folder" size="large" /><h3>{language.t(props.filter.trim() ? "datox.pipeline.workspace.noFilesMatch" : "datox.pipeline.workspace.sourceTitle")}</h3><p>{language.t("datox.pipeline.workspace.sourceHint")}</p><Show when={props.canWrite && !props.filter.trim()}><button type="button" class="workflow-action" data-primary disabled={!!state.action} onClick={() => uploadInput?.click()}>{language.t("datox.pipeline.workspace.upload")}</button></Show></div>}
        >
          {(_fileID) => {
            const file = () => props.detail!
            return (
            <div class="workflow-file-detail">
              <div class="workflow-detail-heading">
                <div class="min-w-0">
                  <h2 class="truncate text-20-semibold text-v2-text-text-base">{file().filename}</h2>
                  <p class="mt-1 text-13-regular text-v2-text-text-muted">
                    {language.t("datox.files.panel.fileMeta", { size: file().size, type: file().content_type })}
                  </p>
                </div>
                <div class="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    class="workflow-action"
                    aria-label={language.t("datox.files.panel.download")}
                    title={language.t("datox.files.panel.download")}
                    onClick={() => downloadFile(file())}
                  >
                    {language.t("datox.files.panel.download")}
                  </button>
                  <Show when={props.canWrite}>
                  <button
                    type="button"
                    class="workflow-action" data-danger disabled={!!state.action}
                    aria-label={language.t("datox.files.panel.delete")}
                    title={language.t("datox.files.panel.delete")}
                    onClick={() => { const selected = file(); void perform("delete", () => props.onDelete(selected)) }}
                  >
                    {language.t("datox.files.panel.delete")}
                  </button>
                  </Show>
                </div>
              </div>
              <Suspense fallback={<TextPreview text={language.t("datox.files.panel.loadingPreview")} />}>
                <FilePreview file={file()} />
              </Suspense>
            </div>
          )}}
        </Show>
      </main>
    </div>
  )
}

function FilePreview(props: { file: Awaited<ReturnType<typeof getFile>> }) {
  if (props.file.content_type.startsWith("image/")) return <ImageFilePreview file={props.file} />
  if (isPdfFile(props.file)) return <PdfFilePreview file={props.file} />
  if (isSpreadsheetFile(props.file)) return <SpreadsheetFilePreview file={props.file} />
  if (isCsvFile(props.file)) return <TextFilePreview file={props.file} format="csv" />
  if (isJsonFile(props.file)) return <TextFilePreview file={props.file} format="json" />
  if (isPresentationFile(props.file)) return <PresentationFilePreview file={props.file} />
  return <FilePartsPreview file={props.file} />
}

function fileContentVersion(file: DatoxFile) {
  return JSON.stringify([file.project_id, file.id, file.updated_at])
}

function TextFilePreview(props: { file: Awaited<ReturnType<typeof getFile>>; format: "csv" | "json" }) {
  const language = useLanguage()
  const [text] = createResource(() => fileContentVersion(props.file), async () => (await getFileContentBlob(props.file.project_id, props.file.id)).text())
  return (
    <FilePreviewSection file={props.file}>
      <Show when={text.latest !== undefined} fallback={<TextPreview text={language.t("datox.files.panel.loadingPreview")} />}>
        {props.format === "csv" ? <CsvTextPreview text={text.latest ?? ""} /> : <JsonTextPreview text={text.latest ?? ""} />}
      </Show>
    </FilePreviewSection>
  )
}

function PresentationFilePreview(props: { file: Awaited<ReturnType<typeof getFile>> }) {
  const language = useLanguage()
  const [blob] = createResource(() => fileContentVersion(props.file), () => getFileContentBlob(props.file.project_id, props.file.id))
  return (
    <FilePreviewSection file={props.file}>
      <Show when={blob.latest} fallback={<TextPreview text={language.t("datox.files.panel.loadingPreview")} />}>
        {(value) => <PptxPreview blob={value()} empty={language.t("datox.files.panel.noPreview")} loading={language.t("datox.files.panel.loadingPreview")} slideLabel={(index) => language.t("datox.files.panel.slide", { index })} />}
      </Show>
    </FilePreviewSection>
  )
}

function PptxPreview(props: { blob: Blob; empty: string; loading: string; slideLabel: (index: number) => string }) {
  let container: HTMLDivElement | undefined
  const [state, setState] = createStore({ failed: false, loading: true, slides: [] as PptxSlide[] })
  createEffect(() => {
    setState({ failed: false, loading: true, slides: [] })
    void renderPptx(props.blob, container).then(() => setState("loading", false)).catch(async () => {
      setState({ failed: true, loading: false, slides: await extractPptxSlides(props.blob).catch(() => []) })
    })
  })
  return (
    <Show when={!state.failed} fallback={<PresentationDeckPreview slides={state.slides} empty={props.empty} slideLabel={props.slideLabel} />}>
      <div class="max-h-[720px] overflow-y-auto overflow-x-hidden rounded-md bg-[#f3f4f6] p-4 [scrollbar-color:#777_transparent] [scrollbar-width:thin]">
        <Show when={state.loading}>
          <TextPreview text={props.loading} />
        </Show>
        <div ref={container} class="mx-auto max-w-full" style={{ width: "min(100%, 640px)" }} />
      </div>
    </Show>
  )
}

function PresentationDeckPreview(props: { slides: PptxSlide[]; empty: string; slideLabel: (index: number) => string }) {
  if (props.slides.length === 0) return <TextPreview text={props.empty} />
  return (
    <div class="max-h-[720px] space-y-4 overflow-auto rounded-md bg-[#202020] p-4 [scrollbar-color:#777_transparent] [scrollbar-width:thin]">
      <For each={props.slides}>
        {(slide, index) => (
          <article class="mx-auto flex min-h-[360px] max-w-4xl flex-col rounded-xl bg-white p-8 text-[#111827] shadow-lg">
            <div class="mb-5 text-12-medium uppercase tracking-[0.2em] text-[#64748b]">{props.slideLabel(index() + 1)}</div>
            <Show when={slide.title}>
              <h4 class="mb-5 text-28-semibold leading-tight text-[#0f172a]">{slide.title}</h4>
            </Show>
            <div class="space-y-3 text-16-regular leading-relaxed text-[#1f2937]">
              <For each={slide.lines}>
                {(line) => <p>{line}</p>}
              </For>
            </div>
          </article>
        )}
      </For>
    </div>
  )
}

function FilePreviewSection(props: { file: Awaited<ReturnType<typeof getFile>>; children: JSX.Element }) {
  return (
    <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h3 class="truncate text-14-medium text-v2-text-text-base">{props.file.filename}</h3>
        <span class="shrink-0 text-12-regular text-v2-text-text-muted">{props.file.content_type}</span>
      </div>
      {props.children}
    </section>
  )
}

function PdfFilePreview(props: { file: Awaited<ReturnType<typeof getFile>> }) {
  const language = useLanguage()
  const [blob] = createResource(() => fileContentVersion(props.file), () => getFileContentBlob(props.file.project_id, props.file.id))
  return (
    <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h3 class="truncate text-14-medium text-v2-text-text-base">{props.file.filename}</h3>
        <span class="shrink-0 text-12-regular text-v2-text-text-muted">{props.file.content_type}</span>
      </div>
      <Show when={blob.latest} fallback={<TextPreview text={language.t("datox.files.panel.loadingPreview")} />}>
        {(value) => <PdfBlobPreview blob={value()} unavailable={language.t("datox.pipeline.report.previewUnavailable")} />}
      </Show>
    </section>
  )
}

function SpreadsheetFilePreview(props: { file: Awaited<ReturnType<typeof getFile>> }) {
  const language = useLanguage()
  const [blob] = createResource(() => fileContentVersion(props.file), () => getFileContentBlob(props.file.project_id, props.file.id))
  return (
    <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h3 class="truncate text-14-medium text-v2-text-text-base">{props.file.filename}</h3>
        <span class="shrink-0 text-12-regular text-v2-text-text-muted">{props.file.content_type}</span>
      </div>
      <Show when={blob.latest} fallback={<TextPreview text={language.t("datox.files.panel.loadingPreview")} />}>
        {(value) => <SpreadsheetPreview blob={value()} />}
      </Show>
    </section>
  )
}

function isSpreadsheetFile(file: { filename: string; content_type: string }) {
  return /spreadsheet|excel/i.test(file.content_type) || /\.xlsx?$/i.test(file.filename)
}

function isPdfFile(file: { filename: string; content_type: string }) {
  return file.content_type === "application/pdf" || /\.pdf$/i.test(file.filename)
}

function isCsvFile(file: { filename: string; content_type: string }) {
  return /csv/i.test(file.content_type) || /\.csv$/i.test(file.filename)
}

function isJsonFile(file: { filename: string; content_type: string }) {
  return /json/i.test(file.content_type) || /\.json$/i.test(file.filename)
}

function isPresentationFile(file: { filename: string; content_type: string }) {
  return /presentation|powerpoint/i.test(file.content_type) || /\.pptx$/i.test(file.filename)
}

function downloadFile(file: DatoxFile) {
  void getFileContentBlob(file.project_id, file.id).then((blob) => {
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = file.filename
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  })
}

function FilePartsPreview(props: { file: Awaited<ReturnType<typeof getFile>> }) {
  const language = useLanguage()
  return (
    <For each={props.file.parts} fallback={<p class="text-14-regular text-v2-text-text-muted">{language.t("datox.files.panel.noPreview")}</p>}>
      {(part) => (
        <section class="mb-4 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
          <div class="mb-3 flex items-center justify-between gap-3">
            <h3 class="text-14-medium text-v2-text-text-base">{part.name}</h3>
            <span class="text-12-regular text-v2-text-text-muted">{part.part_type}</span>
          </div>
          <Show when={sheetRows(part)} fallback={<TextPreview text={part.preview_text || language.t("datox.files.panel.noPreview")} />}>
            {(rows) => <CsvPreview rows={rows()} />}
          </Show>
        </section>
      )}
    </For>
  )
}

function ImageFilePreview(props: { file: DatoxFile }) {
  const language = useLanguage()
  const [url] = createResource(
    () => fileContentVersion(props.file),
    async () => URL.createObjectURL(await getFileContentBlob(props.file.project_id, props.file.id)),
  )
  createEffect(() => {
    const value = url.latest
    if (!value) return
    onCleanup(() => URL.revokeObjectURL(value))
  })
  return (
    <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h3 class="truncate text-14-medium text-v2-text-text-base">{props.file.filename}</h3>
        <span class="shrink-0 text-12-regular text-v2-text-text-muted">{props.file.content_type}</span>
      </div>
      <Show when={url.latest} fallback={<TextPreview text={language.t("datox.files.panel.loadingPreview")} />}>
        {(src) => <img src={src()} alt={props.file.filename} class="max-h-[70vh] max-w-full rounded-md border border-v2-border-border-base object-contain" />}
      </Show>
    </section>
  )
}

function PipelineTabButton(props: { active: boolean; label: string; count?: number; completed?: boolean; working?: boolean; approved?: boolean; onClick: () => void }) {
  const language = useLanguage()
  const statusLabel = () => language.t(props.working ? "datox.workflow.tab.working" : props.approved ? "datox.workflow.tab.approved" : "datox.workflow.tab.saved")
  return (
    <button
      type="button"
      class="workflow-tab"
      data-active={props.active}
      aria-pressed={props.active}
      title={props.working || props.completed ? statusLabel() : undefined}
      onClick={props.onClick}
    >
      {props.label}
      <Show when={props.count !== undefined}><span class="workflow-tab__count">{props.count}</span></Show>
      <Show when={props.working || props.completed}><span class="workflow-tab__status" data-working={!!props.working} aria-hidden="true"><Show when={!props.working}><IconV2 name="check" size="small" /></Show></span><span class="sr-only">{statusLabel()}</span></Show>
    </button>
  )
}

function DeferredExtractionView(props: { render: () => JSX.Element }) {
  const language = useLanguage()
  const [state, setState] = createStore({ ready: false })
  onMount(() => {
    // Let the loading indicator paint before mounting a data-heavy inspector.
    let frame = requestAnimationFrame(() => { frame = requestAnimationFrame(() => setState("ready", true)) })
    onCleanup(() => cancelAnimationFrame(frame))
  })
  return <Show when={state.ready} fallback={<DatoxContentLoading title={language.t("datox.pipeline.extraction.loading")} detail={language.t("datox.pipeline.extraction.loadingHint")} />}>{(_ready) => props.render()}</Show>
}

function RecordsTab(props: { title: string; records: Array<DatoxExtraction | DatoxTransformation>; kind: "extraction" | "transformation"; files?: DatoxFile[]; refreshing?: boolean; onPrevious?: () => void }) {
  const language = useLanguage()
  const [store, setStore] = createStore({ query: "", status: "", selectedID: "" })
  const records = createMemo(() => [...props.records].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at)))
  const statuses = createMemo(() => Array.from(new Set(records().map((record) => record.status).filter(Boolean))))
  const query = createDeferred(() => store.query.trim().toLowerCase(), { timeoutMs: 200 })
  const filtered = createMemo(() => {
    return records().filter((record) => {
      const matchesStatus = !store.status || record.status === store.status
      const matchesQuery = !query() || recordSearchText(record, props.kind, props.files ?? []).includes(query())
      return matchesStatus && matchesQuery
    })
  })
  const selected = createMemo(() => filtered().find((record) => record.id === store.selectedID) ?? filtered().find((record) => recordTableCount(record, props.kind) > 0) ?? filtered()[0])

  createEffect(() => {
    const record = selected()
    if (record && record.id !== store.selectedID) setStore("selectedID", record.id)
  })

  return (
    <div class="workflow-split">
      <aside class="workflow-sidebar workflow-sidebar--padded">
        <div class="mb-4 flex shrink-0 items-start justify-between gap-3">
          <div>
            <h2 class="text-18-semibold text-v2-text-text-base">{props.title}</h2>
            <p class="mt-1 text-12-regular text-v2-text-text-muted">{language.t("datox.pipeline.records.count", { count: filtered().length })}</p>
            <Show when={props.refreshing}><p role="status" class="text-12-regular text-v2-text-text-muted">{language.t("datox.pipeline.extraction.refreshing")}</p></Show>
          </div>
          <Show when={records()[0]}>
            {(record) => <StatusBadge status={record().status} label={language.t("datox.pipeline.records.latest")} />}
          </Show>
        </div>
        <div class="mb-3 flex shrink-0 flex-col gap-2">
          <input
            class="h-9 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-13-regular text-v2-text-text-base outline-none"
            value={store.query}
            placeholder={language.t("datox.pipeline.records.search")}
            aria-label={language.t("datox.pipeline.records.search")}
            onInput={(event) => setStore("query", event.currentTarget.value)}
          />
          <select
            class="h-9 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-13-regular text-v2-text-text-base outline-none"
            value={store.status}
            aria-label={language.t("datox.pipeline.records.allStatuses")}
            onChange={(event) => setStore("status", event.currentTarget.value)}
          >
            <option value="">{language.t("datox.pipeline.records.allStatuses")}</option>
            <For each={statuses()}>{(status) => <option value={status}>{status}</option>}</For>
          </select>
        </div>
        <div class="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1 [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
          <For each={filtered()} fallback={<p class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4 text-13-regular text-v2-text-text-muted">{language.t(`datox.pipeline.${props.kind}.empty`)}</p>}>
            {(record, index) => (
              <button
                type="button"
                class="w-full rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3 text-left hover:bg-v2-background-bg-layer-02"
                classList={{ "border-blue-400/60 bg-v2-background-bg-layer-03": selected()?.id === record.id }}
                aria-pressed={selected()?.id === record.id}
                data-workflow-record
                onClick={() => setStore("selectedID", record.id)}
              >
                <div class="mb-2 flex items-start justify-between gap-2">
                  <div class="min-w-0">
                    <h3 class="truncate text-13-medium text-v2-text-text-base">{recordTitle(record, props.kind)}</h3>
                    <p class="mt-1 truncate text-12-regular text-v2-text-text-muted">{recordSourceLabel(record, props.kind, props.files ?? [])}</p>
                  </div>
                  <Show when={index() === 0}>
                    <span class="shrink-0 rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] text-blue-300">{language.t("datox.pipeline.records.latest")}</span>
                  </Show>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <StatusBadge status={record.status} />
                  <span class="text-11-regular text-v2-text-text-muted">{recordTableCount(record, props.kind)} {language.t("datox.pipeline.records.tables")}</span>
                  <span class="text-11-regular text-v2-text-text-muted">{formatRecordDate(record.created_at)}</span>
                </div>
              </button>
            )}
          </For>
        </div>
      </aside>
      <main class="workflow-inspector">
        <Show when={selected()} fallback={<div class="workflow-empty"><IconV2 name="report" size="large" /><h3>{language.t(records().length ? "datox.pipeline.workspace.noResults" : `datox.pipeline.${props.kind}.empty`)}</h3><p>{language.t(props.kind === "extraction" ? "datox.pipeline.workspace.extractionHint" : "datox.pipeline.workspace.transformationHint")}</p><Show when={records().length > 0} fallback={<Show when={props.onPrevious}><button type="button" class="workflow-action" onClick={props.onPrevious}>{language.t(props.kind === "extraction" ? "datox.pipeline.workspace.viewFiles" : "datox.pipeline.workspace.viewExtractions")}</button></Show>}><button type="button" class="workflow-action" onClick={() => setStore({ query: "", status: "" })}>{language.t("datox.listing.clear")}</button></Show></div>}>
           {(_record) => <Show when={selected()?.id} keyed>{(_id) => <Show when={props.kind === "extraction"} fallback={<RecordInspector record={selected()!} records={filtered()} kind={props.kind} files={props.files ?? []} />}><DeferredExtractionView render={() => <RecordInspector record={selected()!} records={filtered()} kind={props.kind} files={props.files ?? []} />} /></Show>}</Show>}
        </Show>
      </main>
    </div>
  )
}

function RecordInspector(props: { record: DatoxExtraction | DatoxTransformation; records: Array<DatoxExtraction | DatoxTransformation>; kind: "extraction" | "transformation"; files: DatoxFile[] }) {
  const language = useLanguage()
  const extractionRecordCount = createMemo(() => props.kind === "extraction" ? extractionTables(props.record as DatoxExtraction, props.records as DatoxExtraction[]).length : recordTableCount(props.record, props.kind))
  return (
    <div class="mx-auto max-w-5xl">
      <section class="mb-4 rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
        <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <h2 class="truncate text-20-semibold text-v2-text-text-base">{recordTitle(props.record, props.kind)}</h2>
            <p class="mt-1 text-13-regular text-v2-text-text-muted">{recordSubtitle(props.record, props.kind)}</p>
            <Show when={props.kind === "extraction"}>
              <p class="mt-1 text-13-regular text-v2-text-text-muted">{recordSourceLabel(props.record, props.kind, props.files)}</p>
            </Show>
          </div>
          <StatusBadge status={props.record.status} />
        </div>
        <div class="grid gap-3 sm:grid-cols-4">
          <MetricCard label={language.t("datox.pipeline.records.metric.tables")} value={String(extractionRecordCount())} />
          <MetricCard label={language.t("datox.pipeline.records.metric.status")} value={props.record.status} />
          <Show when={props.kind === "extraction"}>
            <MetricCard label={language.t("datox.pipeline.records.metric.sourceFile")} value={recordSourceLabel(props.record, props.kind, props.files)} />
          </Show>
          <MetricCard label={language.t("datox.pipeline.records.metric.created")} value={formatRecordDate(props.record.created_at)} />
          <MetricCard label={language.t("datox.pipeline.records.metric.updated")} value={formatRecordDate(props.record.updated_at)} />
        </div>
      </section>
      {props.kind === "extraction" ? <ExtractionRecord record={props.record as DatoxExtraction} records={props.records as DatoxExtraction[]} /> : <TransformationRecord record={props.record as DatoxTransformation} />}
    </div>
  )
}

function MetricCard(props: { label: string; value: string }) {
  return (
    <div class="workflow-metric rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
      <div class="text-15-medium text-v2-text-text-base">{props.value}</div>
      <div class="mt-1 text-11-regular text-v2-text-text-muted">{props.label}</div>
    </div>
  )
}

function StatusBadge(props: { status: string; label?: string }) {
  return (
    <span data-workflow-status={props.status} class="inline-flex w-fit rounded-full border border-v2-border-border-base px-2 py-0.5 text-11-medium text-v2-text-text-muted">
      {props.label ? `${props.label} · ${props.status}` : props.status}
    </span>
  )
}

function recordTitle(record: DatoxExtraction | DatoxTransformation, kind: "extraction" | "transformation") {
  if (kind === "extraction") return (record as DatoxExtraction).schema_name || "Extraction"
  return (record as DatoxTransformation).instructions || "Transformation"
}

function recordSubtitle(record: DatoxExtraction | DatoxTransformation, kind: "extraction" | "transformation") {
  if (kind === "extraction") return (record as DatoxExtraction).prompt || record.id
  return stringValue((record as DatoxTransformation).output_json.summary) || record.id
}

function recordTableCount(record: DatoxExtraction | DatoxTransformation, kind: "extraction" | "transformation") {
  return kind === "extraction" ? declaredExtractionTableCount((record as DatoxExtraction).result_json.tables) ?? extractionTables(record as DatoxExtraction).length : outputTables(record as DatoxTransformation).length
}

function recordSourceLabel(record: DatoxExtraction | DatoxTransformation, kind: "extraction" | "transformation", files: DatoxFile[]) {
  if (kind !== "extraction") return recordSubtitle(record, kind)
  const fileID = (record as DatoxExtraction).file_id
  if (fileID) return files.find((file) => file.id === fileID)?.filename || fileID
  const sources = extractionSourceLabels(record as DatoxExtraction, files)
  if (sources.length === 1) return sources[0]
  if (sources.length > 1) return `${sources.length} source files`
  return "Multiple files"
}

function extractionSourceLabels(record: DatoxExtraction, files: DatoxFile[]) {
  const values = sourceValues(record.result_json).filter(Boolean)
  const fileIDs = new Set(files.map((file) => file.id))
  const filenames = new Set(files.map((file) => file.filename))
  const labels = values.flatMap((value) => {
    if (fileIDs.has(value)) return [files.find((file) => file.id === value)?.filename || value]
    if (filenames.has(value)) return [value]
    return []
  })
  return Array.from(new Set(labels))
}

function sourceValues(value: unknown): string[] {
  if (typeof value === "string") return [value]
  if (Array.isArray(value)) return value.flatMap(sourceValues)
  if (!isRecord(value)) return []
  return Object.entries(value).flatMap(([key, item]) => {
    const normalized = key.toLowerCase()
    if (["file_id", "file_ids", "filename", "filenames", "source_file", "source_files", "source_filename", "source_filenames"].includes(normalized)) return sourceValues(item)
    return sourceValues(item)
  })
}

function recordSearchText(record: DatoxExtraction | DatoxTransformation, kind: "extraction" | "transformation", files: DatoxFile[]) {
  return `${recordTitle(record, kind)} ${recordSubtitle(record, kind)} ${recordSourceLabel(record, kind, files)} ${record.status} ${JSON.stringify(kind === "extraction" ? (record as DatoxExtraction).result_json : (record as DatoxTransformation).output_json)}`.toLowerCase()
}

function formatRecordDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return date.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
}

function ExtractionRecord(props: { record: DatoxExtraction; records: DatoxExtraction[] }) {
  const language = useLanguage()
  const [store, setStore] = createStore({ tableIndex: 0 })
  const tables = createMemo(() => extractionTables(props.record, props.records))
  const selectedTable = createMemo(() => tables()[store.tableIndex] ?? tables()[0])
  const totalRows = createMemo(() => tables().reduce((total, table) => total + table.rows.length, 0))
  return (
    <section class="mb-4 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0">
          <h3 class="text-14-medium text-v2-text-text-base">{props.record.schema_name ?? "Extraction"}</h3>
          <p class="mt-1 text-12-regular text-v2-text-text-muted">
            {language.t("datox.pipeline.extraction.categorySummary", { tables: tables().length, rows: totalRows() })}
          </p>
        </div>
        <StatusBadge status={props.record.status} />
      </div>
      <Show when={tables().length > 0} fallback={<p class="text-14-regular text-v2-text-text-muted">{language.t("datox.pipeline.extraction.noTable")}</p>}>
        <div class="grid gap-4 xl:grid-cols-[260px_minmax(0,1fr)]">
          <aside class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
            <div class="mb-3 text-12-medium text-v2-text-text-muted">{language.t("datox.pipeline.extraction.tables")}</div>
            <div class="max-h-[420px] space-y-2 overflow-y-auto pr-1 [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-width:thin]">
              <For each={tables()}>
                {(table, index) => (
                  <button
                    type="button"
                    class="w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 py-2 text-left hover:bg-v2-background-bg-layer-02"
                    classList={{ "border-blue-400/60 bg-v2-background-bg-layer-03": selectedTable()?.name === table.name }}
                    onClick={() => setStore("tableIndex", index())}
                  >
                    <div class="truncate text-12-medium text-v2-text-text-base">{table.name}</div>
                    <div class="mt-1 text-11-regular text-v2-text-text-muted">{language.t("datox.pipeline.extraction.tableMeta", { rows: table.rows.length, columns: table.headers.length })}</div>
                  </button>
                )}
              </For>
            </div>
          </aside>
          <main class="min-w-0">
            <Show keyed when={selectedTable()}>
              {(table) => (
                <div>
                  <div class="mb-3 grid gap-3 sm:grid-cols-3">
                    <MetricCard label={language.t("datox.pipeline.extraction.table")} value={table.name} />
                    <MetricCard label={language.t("datox.pipeline.output.rows")} value={String(table.rows.length)} />
                    <MetricCard label={language.t("datox.pipeline.output.columns")} value={String(table.headers.length)} />
                  </div>
                  <div class="mb-3 flex justify-end">
                    <button
                      type="button"
                      class="flex size-8 items-center justify-center rounded-md border border-v2-border-border-base text-v2-icon-icon-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-icon-icon-base"
                      aria-label={language.t("datox.pipeline.output.downloadCsv")}
                      title={language.t("datox.pipeline.output.downloadCsv")}
                      onClick={() => downloadExtractionTable(table)}
                    >
                      <IconV2 name="download" size="small" />
                    </button>
                  </div>
                  <CsvPreview table={table} />
                  <p class="mt-2 text-11-regular text-v2-text-text-muted">{language.t("datox.pipeline.extraction.pagedHint")}</p>
                </div>
              )}
            </Show>
          </main>
        </div>
      </Show>
    </section>
  )
}

function downloadExtractionTable(table: { name: string; headers: string[]; rows: Array<Record<string, string>> }) {
  const csv = [table.headers, ...table.rows.map((row) => table.headers.map((header) => row[header] ?? ""))]
    .map((row) => row.map(escapeCsvCell).join(","))
    .join("\n")
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = `${safeFilename(table.name || "extraction")}.csv`
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

function escapeCsvCell(value: string) {
  if (!/[",\n\r]/.test(value)) return value
  return `"${value.replaceAll('"', '""')}"`
}

function safeFilename(value: string) {
  return value.replace(/[^a-z0-9._-]+/gi, "_").replace(/^_+|_+$/g, "") || "extraction"
}

function TransformationRecord(props: { record: DatoxTransformation }) {
  const language = useLanguage()
  const data = () => props.record.output_json
  const tables = () => outputTables(props.record)
  const steps = () => structuredList(data().steps ?? props.record.steps_json)
  const validations = () => structuredList(data().validations)
  const sourceTables = () => stringList(data().source_tables)
  const assumptions = () => stringList(data().assumptions)
  const summary = () => stringValue(data().summary)
  return (
    <section class="mb-4 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 class="text-14-medium text-v2-text-text-base">{props.record.instructions || "Transformation"}</h3>
          <p class="mt-1 text-12-regular text-v2-text-text-muted">{tables().length} output tables</p>
        </div>
        <span class="text-12-regular text-v2-text-text-muted">{props.record.status}</span>
      </div>
      <Show when={summary()}>
        <p class="mb-4 text-13-regular text-v2-text-text-base">{summary()}</p>
      </Show>
      <TransformationSection title={language.t("datox.pipeline.transformation.sources")} items={sourceTables()} />
      <TransformationSteps title={language.t("datox.pipeline.transformation.steps")} items={steps()} />
      <TransformationSteps title={language.t("datox.pipeline.transformation.validations")} items={validations()} />
      <TransformationSection title={language.t("datox.pipeline.transformation.assumptions")} items={assumptions()} />
    </section>
  )
}

function OutputTab(props: { transformations: DatoxTransformation[]; artifacts: DatoxArtifact[]; onTransformations?: () => void }) {
  const language = useLanguage()
  const [store, setStore] = createStore({ selectedKey: "", query: "", reportID: "", copyState: "" })
  createEffect(() => {
    if (store.copyState !== "copied") return
    const timer = setTimeout(() => setStore("copyState", ""), 2000)
    onCleanup(() => clearTimeout(timer))
  })
  const reportOptions = createMemo(() => props.artifacts.filter((artifact) => artifact.artifact_type === "report"))
  const latestTransformation = createMemo(() =>
    [...props.transformations]
      .sort((a, b) => Date.parse(b.updated_at || b.created_at) - Date.parse(a.updated_at || a.created_at))
      .find((transformation) => outputTables(transformation).length > 0),
  )
  const tables = createMemo(() =>
    [...props.transformations]
      .sort((a, b) => Date.parse(b.updated_at || b.created_at) - Date.parse(a.updated_at || a.created_at))
      .flatMap((transformation) =>
        outputTables(transformation).map((table, index) => ({
          ...table,
          key: `${transformation.id}:${index}`,
          transformation,
          latest: transformation.id === latestTransformation()?.id,
          reports: reportsForOutputTable(props.artifacts, props.transformations, transformation, table.name),
        })),
      ),
  )
  const filteredTables = createMemo(() => store.reportID ? tables().filter((table) => table.reports.some((report) => report.id === store.reportID)) : tables())
  const selected = createMemo(() => filteredTables().find((table) => table.key === store.selectedKey) ?? filteredTables()[0])
  const filteredRows = createMemo(() => {
    const table = selected()
    const query = store.query.trim().toLowerCase()
    if (!table) return []
    if (!query) return table.rows
    return table.rows.filter((row) => table.headers.some((header) => (row[header] ?? "").toLowerCase().includes(query)))
  })

  createEffect(() => {
    const table = selected()
    if (table && table.key !== store.selectedKey) setStore("selectedKey", table.key)
  })

  const copyTable = () => {
    const table = selected()
    if (!table) return
    void navigator.clipboard.writeText(tableToCsv(table.headers, filteredRows())).then(() => setStore("copyState", "copied")).catch(() => setStore("copyState", "error"))
  }

  const downloadTable = () => {
    const table = selected()
    if (!table) return
    const url = URL.createObjectURL(new Blob([tableToCsv(table.headers, filteredRows())], { type: "text/csv;charset=utf-8" }))
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = `${table.name || "output"}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div class="workflow-split">
      <aside class="workflow-sidebar workflow-sidebar--padded">
        <div class="mb-4 shrink-0">
          <h2 class="text-18-semibold text-v2-text-text-base">{language.t("datox.pipeline.output")}</h2>
          <p class="mt-1 text-12-regular text-v2-text-text-muted">{language.t("datox.pipeline.output.count", { count: filteredTables().length })}</p>
        </div>
        <div class="mb-3 shrink-0">
          <label class="mb-1 block text-11-medium text-v2-text-text-muted" for="datox-output-report-filter">{language.t("datox.pipeline.output.filterByReport")}</label>
          <select
            id="datox-output-report-filter"
            class="h-9 w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-13-regular text-v2-text-text-base outline-none"
            value={store.reportID}
            onChange={(event) => setStore({ reportID: event.currentTarget.value, selectedKey: "" })}
          >
            <option value="">{language.t("datox.pipeline.output.allReports")}</option>
            <For each={reportOptions()}>{(report) => <option value={report.id}>{reportOptionLabel(report)}</option>}</For>
          </select>
        </div>
        <div class="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1 [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
          <For each={filteredTables()} fallback={<p class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4 text-13-regular text-v2-text-text-muted">{language.t("datox.pipeline.output.empty")}</p>}>
            {(table) => (
              <button
                type="button"
                class="w-full rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3 text-left hover:bg-v2-background-bg-layer-02"
                classList={{ "border-blue-400/60 bg-v2-background-bg-layer-03": selected()?.key === table.key }}
                aria-pressed={selected()?.key === table.key}
                data-workflow-record
                onClick={() => setStore("selectedKey", table.key)}
              >
                <h3 class="truncate text-13-medium text-v2-text-text-base">{table.name}</h3>
                <div class="mt-2 flex flex-wrap gap-2 text-11-regular text-v2-text-text-muted">
                  <Show when={table.latest}>
                    <span class="rounded-full bg-blue-500/10 px-2 py-0.5 text-blue-300">{language.t("datox.pipeline.records.latest")}</span>
                  </Show>
                  <span>{table.rows.length} {language.t("datox.pipeline.output.rows")}</span>
                  <span>{table.headers.length} {language.t("datox.pipeline.output.columns")}</span>
                </div>
                <p class="mt-2 truncate text-11-regular text-v2-text-text-muted">{reportUsageLabel(table.reports, language)}</p>
              </button>
            )}
          </For>
        </div>
      </aside>
      <main class="workflow-inspector">
        <Show keyed when={selected()} fallback={<div class="workflow-empty"><IconV2 name="report" size="large" /><h3>{language.t("datox.pipeline.output.empty")}</h3><p>{language.t("datox.pipeline.workspace.outputHint")}</p><Show when={store.reportID} fallback={<Show when={props.onTransformations}><button type="button" class="workflow-action" onClick={props.onTransformations}>{language.t("datox.pipeline.workspace.viewTransformations")}</button></Show>}><button type="button" class="workflow-action" onClick={() => setStore("reportID", "")}>{language.t("datox.listing.clear")}</button></Show></div>}>
          {(table) => (
            <div class="mx-auto max-w-6xl">
              <section class="mb-4 rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
                <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
                  <div class="min-w-0">
                    <h2 class="truncate text-20-semibold text-v2-text-text-base">{table.name}</h2>
                    <p class="mt-1 text-13-regular text-v2-text-text-muted">{table.transformation.instructions || language.t("datox.pipeline.output.sourceTransformation")}</p>
                  </div>
                  <div class="flex gap-2">
                    <button class="rounded-md border border-v2-border-border-base px-3 py-1.5 text-12-medium text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base" onClick={copyTable}>
                      {language.t(store.copyState === "copied" ? "datox.pipeline.workspace.copied" : "datox.pipeline.output.copy")}
                    </button>
                    <button class="rounded-md border border-v2-border-border-base px-3 py-1.5 text-12-medium text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base" onClick={downloadTable}>
                      {language.t("datox.pipeline.output.downloadCsv")}
                    </button>
                  </div>
                </div>
                <Show when={store.copyState === "error"}><p role="alert" class="workflow-error">{language.t("datox.pipeline.workspace.copyError")}</p></Show>
                <div class="mb-4 grid gap-3 sm:grid-cols-4">
                  <MetricCard label={language.t("datox.pipeline.output.rows")} value={String(table.rows.length)} />
                  <MetricCard label={language.t("datox.pipeline.output.columns")} value={String(table.headers.length)} />
                  <MetricCard label={language.t("datox.pipeline.records.metric.status")} value={table.transformation.status} />
                  <MetricCard label={language.t("datox.pipeline.records.metric.updated")} value={formatRecordDate(table.transformation.updated_at)} />
                </div>
                <div class="mb-4 grid gap-3 sm:grid-cols-2">
                  <MetricCard label={language.t("datox.pipeline.output.version")} value={table.latest ? language.t("datox.pipeline.records.latest") : formatRecordDate(table.transformation.created_at)} />
                  <MetricCard label={language.t("datox.pipeline.output.reportUsage")} value={reportUsageLabel(table.reports, language)} />
                </div>
                <input
                  class="h-9 w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 text-13-regular text-v2-text-text-base outline-none"
                  value={store.query}
                   placeholder={language.t("datox.pipeline.output.search")}
                   aria-label={language.t("datox.pipeline.output.search")}
                  onInput={(event) => setStore("query", event.currentTarget.value)}
                />
              </section>
              <CsvPreview table={{ ...table, rows: filteredRows() }} />
            </div>
          )}
        </Show>
      </main>
    </div>
  )
}

function reportsForOutputTable(artifacts: DatoxArtifact[], transformations: DatoxTransformation[], transformation: DatoxTransformation, tableName: string) {
  return artifacts.filter((artifact) => {
    if (artifact.artifact_type !== "report") return false
    return reportUsesOutputTable(artifact, transformations, transformation, tableName)
  })
}

function reportUsesOutputTable(artifact: DatoxArtifact, transformations: DatoxTransformation[], transformation: DatoxTransformation, tableName: string) {
  const metadata = artifact.artifact_metadata ?? {}
  const hasTransformationRefs = metadataHasTransformationReferences(metadata)
  const hasTableRefs = metadataHasTableReferences(metadata)
  if (hasTransformationRefs) return metadataReferencesTransformation(metadata, transformation.id) && (!hasTableRefs || metadataReferencesTable(metadata, tableName))
  if (!hasTableRefs) return false
  const inferred = inferReportTransformation(artifact, transformations)
  if (inferred) return inferred.id === transformation.id && metadataReferencesTable(metadata, tableName)
  return metadataReferencesTable(metadata, tableName)
}

function inferReportTransformation(artifact: DatoxArtifact, transformations: DatoxTransformation[]) {
  const reportTableNames = metadataTableNames(artifact.artifact_metadata ?? {})
  if (reportTableNames.length === 0) return undefined
  const reportCreatedAt = Date.parse(artifact.created_at)
  const candidates = transformations
    .filter((transformation) => Number.isNaN(reportCreatedAt) || Date.parse(transformation.created_at) <= reportCreatedAt)
    .filter((transformation) => {
      const names = outputTables(transformation).map((table) => normalizeTableName(table.name))
      return reportTableNames.every((name) => names.includes(name))
    })
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
  return candidates[0]
}

function metadataHasTransformationReferences(metadata: Record<string, unknown>) {
  return Boolean(metadata.source_transformation_id || metadata.transformation_id || nonEmptyArray(metadata.source_transformation_ids) || nonEmptyArray(metadata.transformation_ids))
}

function metadataHasTableReferences(metadata: Record<string, unknown>) {
  return metadataTableNames(metadata).length > 0
}

function metadataReferencesTransformation(metadata: Record<string, unknown>, transformationID: string) {
  const values = [metadata.source_transformation_id, metadata.transformation_id]
  const lists = [metadata.source_transformation_ids, metadata.transformation_ids]
  return values.some((value) => value === transformationID) || lists.some((value) => Array.isArray(value) && value.includes(transformationID))
}

function metadataReferencesTable(metadata: Record<string, unknown>, tableName: string) {
  const normalized = normalizeTableName(tableName)
  return metadataTableNames(metadata).includes(normalized)
}

function metadataTableNames(metadata: Record<string, unknown>) {
  return [metadata.source_output_tables, metadata.output_tables, metadata.source_tables, metadata.tables].flatMap((value) => {
    if (!Array.isArray(value)) return []
    return value.flatMap((item) => {
      const name = typeof item === "string" ? item : isRecord(item) ? stringValue(item.name) || stringValue(item.table_name) || stringValue(item.title) : ""
      return name ? [normalizeTableName(name)] : []
    })
  })
}

function nonEmptyArray(value: unknown) {
  return Array.isArray(value) && value.length > 0
}

function reportUsageLabel(reports: DatoxArtifact[], language: ReturnType<typeof useLanguage>) {
  if (reports.length === 0) return language.t("datox.pipeline.output.notUsedInReport")
  if (reports.length === 1) return language.t("datox.pipeline.output.usedInReport", { title: reports[0].title })
  return language.t("datox.pipeline.output.usedInReports", { count: reports.length })
}

function reportOptionLabel(report: DatoxArtifact) {
  return `${report.title} · ${formatRecordDate(report.created_at)}`
}

type ExtractedTable = {
  name: string
  headers: string[]
  rows: Array<Record<string, string>>
}

// API extraction snapshots are replaced on refresh, never edited in place.
// Weak keys release normalized rows when their source snapshot is discarded.
const extractionTableCache = new WeakMap<DatoxExtraction, ExtractedTable[]>()

function extractionTables(record: DatoxExtraction, relatedRecords?: DatoxExtraction[]): ExtractedTable[] {
  const primary = extractionTablesFromData(record)
  const related = relatedRecords?.filter((item) => item.period_id === record.period_id) ?? []
  if (related.length <= 1) return primary
  return mergeExtractionTables(related.length > 0 ? related : [record])
}

function extractionTablesFromData(record: DatoxExtraction): ExtractedTable[] {
  const cached = extractionTableCache.get(record)
  if (cached) return cached
  const tables = parseExtractionTables(record)
  extractionTableCache.set(record, tables)
  return tables
}

function parseExtractionTables(record: DatoxExtraction): ExtractedTable[] {
  const data = record.result_json
  const contract = tablesFromCollection(data.tables)
  if (contract.length > 0) return contract
  const nested = nestedTableCollections(data)
  if (nested.length > 0) return nested
  const direct = directTableRows(data).map((table) => ({ ...table, name: record.schema_name ?? table.name }))
  if (direct.length > 0) return direct
  const complianceTables = [...nonFundTables(data.non_funds), ...fundTables(data.funds)]
  if (complianceTables.length > 0) return complianceTables
  return nestedCategoryTables(data.data ?? data).map((table) => ({
    ...table,
    name: record.schema_name && table.name === "Extracted table" ? record.schema_name : table.name,
  }))
}

function mergeExtractionTables(records: DatoxExtraction[]): ExtractedTable[] {
  const byName = new Map<string, { table: ExtractedTable; updatedAt: number }>()
  const ordered = [...records].sort((a, b) => Date.parse(b.updated_at || b.created_at) - Date.parse(a.updated_at || a.created_at))
  for (const record of ordered) {
    const updatedAt = Date.parse(record.updated_at || record.created_at) || 0
    for (const table of extractionTablesFromData(record)) {
      const key = normalizeTableName(table.name)
      const existing = byName.get(key)
      if (!existing || table.rows.length > existing.table.rows.length || (table.rows.length === existing.table.rows.length && updatedAt > existing.updatedAt)) {
        byName.set(key, { table, updatedAt })
      }
    }
  }
  return Array.from(byName.values()).map((entry) => entry.table)
}

function normalizeTableName(value: string) {
  return value.toLowerCase().replace(/^[^·]+·\s*/, "").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "table"
}

function transformationTables(record: DatoxTransformation): ExtractedTable[] {
  const data = record.output_json
  const direct = directTableRows(data)
  if (direct.length > 0) return direct.map((table) => ({ ...table, name: outputTableName(record, table.name) }))
  const tableCollections = [data.tables, data.output_tables, data.outputs, data.result]
  const nested = tableCollections.flatMap((value) => tablesFromCollection(value))
  if (nested.length > 0) return nested.map((table) => ({ ...table, name: outputTableName(record, table.name) }))
  return Object.entries(data).flatMap(([name, value]) => rowsToTable(name, value))
}

function outputTables(record: DatoxTransformation): ExtractedTable[] {
  const raw = record.output_json.output_tables
  const tables = tablesFromCollection(Array.isArray(raw) ? raw.filter((table) => !isRecord(table) || table.visibility !== "internal") : raw)
  if (record.output_json.pipeline_run_id) return tables
  if (tables.length > 0) return tables
  return transformationTables(record)
}

function TransformationSection(props: { title: string; items: string[] }) {
  return (
    <Show when={props.items.length > 0}>
      <div class="mb-4">
        <h4 class="mb-2 text-13-medium text-v2-text-text-base">{props.title}</h4>
        <ul class="list-disc space-y-1 pl-5 text-13-regular text-v2-text-text-muted">
          <For each={props.items}>{(item) => <li>{item}</li>}</For>
        </ul>
      </div>
    </Show>
  )
}

function TransformationSteps(props: { title: string; items: Array<{ label: string; description: string; status?: string }> }) {
  return (
    <Show when={props.items.length > 0}>
      <div class="mb-4">
        <h4 class="mb-2 text-13-medium text-v2-text-text-base">{props.title}</h4>
        <div class="space-y-2">
          <For each={props.items}>
            {(item) => (
              <div class="rounded-md border border-v2-border-border-base bg-v2-background-bg-base p-3">
                <div class="flex items-center justify-between gap-3">
                  <div class="text-13-medium text-v2-text-text-base">{item.label}</div>
                  <Show when={item.status}>
                    {(status) => <span class="text-12-regular text-v2-text-text-muted">{status()}</span>}
                  </Show>
                </div>
                <Show when={item.description}>
                  <p class="mt-1 text-12-regular text-v2-text-text-muted">{item.description}</p>
                </Show>
              </div>
            )}
          </For>
        </div>
      </div>
    </Show>
  )
}

function structuredList(value: unknown): Array<{ label: string; description: string; status?: string }> {
  if (!Array.isArray(value)) return []
  return value.filter(isRecord).map((item, index) => ({
    label: stringValue(item.label) || stringValue(item.name) || `Step ${index + 1}`,
    description: stringValue(item.description) || stringValue(item.details),
    status: stringValue(item.status) || undefined,
  }))
}

function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.map((item) => (typeof item === "string" ? item : isRecord(item) ? stringValue(item.name) || stringValue(item.label) : "")).filter(Boolean)
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : ""
}

function outputTableName(record: DatoxTransformation, fallback: string) {
  const metadataName = record.output_json.table_name ?? record.output_json.name ?? record.output_json.title
  return typeof metadataName === "string" && metadataName.trim() ? metadataName : fallback
}

function tableToCsv(headers: string[], rows: Array<Record<string, string>>) {
  return [headers, ...rows.map((row) => headers.map((header) => row[header] ?? ""))]
    .map((row) => row.map(csvCell).join(","))
    .join("\n")
}

function csvCell(value: string) {
  return /[",\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value
}

function tablesFromCollection(value: unknown): ExtractedTable[] {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => {
      if (!isRecord(item)) return []
      const name = typeof item.name === "string" ? item.name : typeof item.table_name === "string" ? item.table_name : `Output ${index + 1}`
      const rows = rowsToTable(name, item.rows ?? item.data ?? item.values)
      const columns = Array.isArray(item.columns) ? item.columns.filter((column): column is string => typeof column === "string") : []
      if (columns.length && Array.isArray(item.rows) && item.rows.length === 0) return [{ name, headers: columns, rows: [] }]
      if (columns.length === 0 || rows.length === 0) return rows
      return rows.map((table) => ({ ...table, headers: columns }))
    })
  }
  if (isRecord(value)) {
    return Object.entries(value).flatMap(([name, rows]) => {
      const nested = tablesFromCollection(rows)
      if (nested.length > 0) return nested.map((table) => ({ ...table, name: `${prettyLabel(name)} · ${table.name}` }))
      return rowsToTable(name, rows)
    })
  }
  return []
}

function nestedTableCollections(value: unknown, prefix = "", depth = 0): ExtractedTable[] {
  if (depth > 5) return []
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => nestedTableCollections(item, prefix || `Item ${index + 1}`, depth + 1))
  }
  if (!isRecord(value)) return []
  const label = stringValue(value.filename) || stringValue(value.file_name) || stringValue(value.name) || stringValue(value.title) || prefix
  const tableKeys = ["tables", "extracted_tables", "worksheets", "sheets", "output_tables"]
  const direct = tableKeys.flatMap((key) => tablesFromCollection(value[key])).map((table) => ({
    ...table,
    name: label && !table.name.startsWith(`${label} ·`) ? `${label} · ${table.name}` : table.name,
  }))
  if (direct.length > 0) return direct
  return Object.entries(value).flatMap(([key, item]) => nestedTableCollections(item, label || prettyLabel(key), depth + 1))
}

function nestedCategoryTables(value: unknown): ExtractedTable[] {
  if (Array.isArray(value)) return rowsToTable("Extracted table", value)
  if (!isRecord(value)) return []
  return Object.entries(value).flatMap(([name, section]) => {
    const table = rowsToTable(prettyLabel(name), section)
    if (table.length > 0) return table
    return []
  })
}

function prettyLabel(value: string) {
  return value
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function nonFundTables(value: unknown): ExtractedTable[] {
  if (!isRecord(value)) return []
  const data = isRecord(value.data) ? value.data : {}
  return Object.entries(data).flatMap(([name, rows]) => rowsToTable(name, rows))
}

function fundTables(value: unknown): ExtractedTable[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((fund) => {
    if (!isRecord(fund) || !isRecord(fund.data)) return []
    const fundName = typeof fund.name === "string" ? fund.name : "Fund"
    return Object.entries(fund.data).flatMap(([tableName, rows]) =>
      rowsToTable(`${fundName} · ${tableName}`, rows),
    )
  })
}

function directTableRows(data: Record<string, unknown>): ExtractedTable[] {
  const candidates = [data.rows, data.data]
  return candidates.flatMap((rows) => rowsToTable("Extracted table", rows))
}

function rowsToTable(name: string, rows: unknown): ExtractedTable[] {
  if (!Array.isArray(rows) || rows.length === 0) return []
  const normalized = rows.filter(isRecord).map((row) =>
    Object.fromEntries(Object.entries(row).map(([key, value]) => [key, cellText(value)])),
  )
  if (normalized.length === 0) return []
  const headers = Array.from(new Set(normalized.flatMap((row) => Object.keys(row))))
  if (headers.length === 0) return []
  return [{ name, headers, rows: normalized }]
}

function cellText(value: unknown): string {
  if (isRecord(value) && "v" in value) return value.v == null ? "" : String(value.v)
  if (value == null) return ""
  if (typeof value === "object") return JSON.stringify(value)
  return String(value)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function ReportTab(props: { initialArtifactID?: string; artifacts: DatoxArtifact[]; templates: DatoxArtifact[]; loading: boolean; error?: boolean; onRefresh: () => void; onShare: (artifact: DatoxArtifact) => void; onDelete: (artifact: DatoxArtifact) => Promise<void> }) {
  const language = useLanguage()
  const actionClass = "report-workspace__action"
  const [store, setStore] = createStore({ selectedArtifactID: props.initialArtifactID ?? "", revision: 0, downloading: false, exporting: false, exportingPDF: false, actionError: false, exportError: "" })
  const displayArtifacts = createMemo(() => props.loading && !props.artifacts.length ? [] : [...props.artifacts, ...props.templates])
  const selectedArtifact = createMemo(() => displayArtifacts().find((artifact) => artifact.id === store.selectedArtifactID) ?? displayArtifacts()[0])
  const [historical] = createResource(() => store.revision && selectedArtifact() ? `${selectedArtifact()!.project_id}:${selectedArtifact()!.id}:${store.revision}` : false,
    () => getArtifact(selectedArtifact()!.project_id, selectedArtifact()!.id, store.revision))
  const viewedArtifact = () => {
    if (!store.revision) return selectedArtifact()
    if (historical.error) return undefined
    const value = historical.latest
    return value?.id === selectedArtifact()?.id && value?.revision === store.revision ? value : undefined
  }
  createEffect(() => {
    if (props.loading || (!displayArtifacts().length && props.initialArtifactID)) return
    const selected = selectedArtifact()
    if (selected && selected.id !== store.selectedArtifactID) setStore({ selectedArtifactID: selected.id, revision: 0 })
    if (!selected && store.selectedArtifactID) setStore("selectedArtifactID", "")
  })
  const download = async (artifact: DatoxArtifact) => {
    if (store.downloading) return
    setStore({ downloading: true, actionError: false, exportError: "" })
    try {
      const result = await downloadArtifact(artifact.project_id, artifact.id, store.revision || undefined)
      const normalized = await normalizedArtifactDownload(result.blob, artifact.format, result.filename)
      const url = URL.createObjectURL(normalized.blob)
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download = artifactDownloadFilename(artifact, result.filename)
      anchor.click()
      URL.revokeObjectURL(url)
    } catch {
      setStore("actionError", true)
    } finally {
      setStore("downloading", false)
    }
  }
  const exportAs = async (artifact: DatoxArtifact, format: "html" | "txt" | "json" | "csv" | "pdf") => {
    if (store.exporting) return
    setStore({ exporting: true, exportingPDF: format === "pdf", actionError: false, exportError: "" })
    try {
      const result = await exportArtifact(artifact.project_id, artifact.id, format, store.revision || undefined)
      const url = URL.createObjectURL(result.blob)
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download = result.filename
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      setStore({ actionError: true, exportError: error instanceof Error ? error.message : "" })
    } finally {
      setStore({ exporting: false, exportingPDF: false })
    }
  }
  return (
    <div class="report-workspace">
      <div class="report-workspace__toolbar">
        <div class="report-workspace__heading">
          <h2>{language.t("datox.pipeline.report")}</h2><span class="report-workspace__count" aria-label={language.t("datox.pipeline.report.availableCount", { count: displayArtifacts().length })}>{displayArtifacts().length}</span>
        </div>
        <div class="report-workspace__actions">
          <Show when={selectedArtifact()}>{(artifact) => <DatoxRevisionSelect projectID={artifact().project_id} outputID={artifact().id} kind="report" value={store.revision}
            refreshKey={artifact().updated_at} disabled={store.downloading || store.exporting} onChange={(revision) => setStore({ revision, actionError: false, exportError: "" })} />}</Show>
          <Show when={!store.revision && selectedArtifact()}>{(artifact) => <Show when={htmlReportFormat(artifact().format)}><DatoxReportPreviewLink projectID={artifact().project_id} artifactID={artifact().id} /></Show>}</Show>
          <Show when={viewedArtifact()}>
            {(artifact) => (
              <>
                <button type="button" class={actionClass} disabled={store.downloading} aria-busy={store.downloading} onClick={() => void download(artifact())}>{language.t(store.downloading ? "datox.pipeline.report.downloading" : "datox.pipeline.report.download")}</button>
                <MenuV2 gutter={4} placement="bottom-end">
                  <MenuV2.Trigger type="button" class={actionClass} disabled={store.exporting} aria-busy={store.exporting}>{language.t(store.exportingPDF ? "datox.pipeline.report.generatingPDF" : store.exporting ? "datox.pipeline.report.exporting" : "datox.pipeline.report.export")} <span aria-hidden="true">▾</span></MenuV2.Trigger>
                  <MenuV2.Portal>
                    <MenuV2.Content>
                      <Show when={htmlReportFormat(artifact().format) || artifact().format.toLowerCase() === "pdf"}><MenuV2.Item onSelect={() => exportAs(artifact(), "pdf")}>{language.t("datox.pipeline.report.export.pdf")}</MenuV2.Item></Show>
                      <MenuV2.Item onSelect={() => exportAs(artifact(), "html")}>{language.t("datox.pipeline.report.export.html")}</MenuV2.Item>
                      <MenuV2.Item onSelect={() => exportAs(artifact(), "txt")}>{language.t("datox.pipeline.report.export.txt")}</MenuV2.Item>
                      <MenuV2.Item onSelect={() => exportAs(artifact(), "json")}>{language.t("datox.pipeline.report.export.json")}</MenuV2.Item>
                      <MenuV2.Item onSelect={() => exportAs(artifact(), "csv")}>{language.t("datox.pipeline.report.export.csv")}</MenuV2.Item>
                    </MenuV2.Content>
                  </MenuV2.Portal>
                </MenuV2>
                <Show when={!store.revision}><button type="button" class={actionClass} onClick={() => props.onShare(artifact())}>{language.t("datox.pipeline.report.share")}</button>
                <DatoxReportDeleteButton artifact={artifact()} onDelete={props.onDelete} /></Show>
              </>
            )}
          </Show>
          <button type="button" class={actionClass} disabled={props.loading} aria-busy={props.loading} onClick={props.onRefresh}>{language.t("datox.pipeline.report.refresh")}</button>
        </div>
      </div>
      <Show when={store.actionError}><p role="alert" class="report-workspace__error">{store.exportError || language.t("datox.pipeline.report.actionError")}</p></Show>
      <Show when={props.error}><p role="alert" class="report-workspace__error">{language.t("datox.pipeline.report.previewError")}</p></Show>
      <Show when={historical.error}><p role="alert" class="report-workspace__error">{language.t("datox.revisions.error")}</p></Show>
      <Show when={displayArtifacts().length > 0} fallback={<Show when={props.loading} fallback={<Show when={!props.error}><p role="status" class="report-workspace__empty">{language.t("datox.pipeline.report.empty")}</p></Show>}><DatoxContentLoading title={language.t("datox.pipeline.report.opening")} detail={language.t("datox.pipeline.report.loadingHint")} /></Show>}>
        <nav class="report-workspace__reports" aria-label={language.t("datox.pipeline.report.chooseReport")}>
          <For each={displayArtifacts()}>
            {(artifact) => (
              <button
                type="button"
                class="report-workspace__report"
                aria-pressed={selectedArtifact()?.id === artifact.id}
                onClick={() => setStore({ selectedArtifactID: artifact.id, revision: 0, actionError: false, exportError: "" })}
                title={htmlReportFormat(artifact.format) ? reportDisplayTitle(artifact.title) : artifact.title}
              >
                <span class="report-workspace__report-icon" aria-hidden="true"><svg width="18" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"><path d="M14 2H5v20h14V7l-5-5Zm0 0v6h5M8 13h8M8 17h5" /></svg></span><span class="report-workspace__report-copy"><span class="report-workspace__report-name">{htmlReportFormat(artifact.format) ? reportDisplayTitle(artifact.title) : artifact.title}</span>
                <span class="report-workspace__report-type">{artifact.artifact_type === "report_template" ? language.t("datox.pipeline.report.template.badge") : htmlReportFormat(artifact.format) ? language.t("datox.pipeline.report") : artifact.format.toUpperCase()}</span></span>
              </button>
            )}
          </For>
        </nav>
        <Show when={selectedArtifact() ? `${selectedArtifact()!.id}:${store.revision}` : undefined} keyed>
          {(_id) => (
            <section class="report-workspace__preview" aria-label={selectedArtifact()!.title}>
              <Show when={viewedArtifact()} fallback={<p role="status">{language.t("datox.revisions.loading")}</p>}>{(artifact) =>
                <ReportPreview artifact={artifact()} revision={store.revision || undefined} />
              }</Show>
            </section>
          )}
        </Show>
      </Show>
    </div>
  )
}

function ReportPreview(props: { artifact: DatoxArtifact; revision?: number }) {
  const language = useLanguage()
  const format = () => normalizedReportFormat(props.artifact.format)
  const [download, downloadActions] = createResource(
    () => (previewableReportFormat(format()) ? `${props.artifact.id}:${props.artifact.updated_at}:${props.revision ?? "latest"}` : undefined),
    () => downloadArtifact(props.artifact.project_id, props.artifact.id, props.revision),
  )
  const previewText = () => download()?.blob ? undefined : reportPreviewText(props.artifact)
  return (
    <Show when={!download.error} fallback={<div role="alert" class="report-workspace__empty"><p>{language.t("datox.pipeline.report.previewError")}</p><button type="button" class="report-workspace__action" onClick={() => void Promise.resolve(downloadActions.refetch()).catch(() => undefined)}>{language.t("datox.pipeline.report.refresh")}</button></div>}>
    <Show when={!download.loading || download.latest} fallback={<p role="status" class="report-workspace__empty">{language.t("common.loading")}</p>}>
    <Show when={download()?.blob} fallback={<FormatPreview artifact={props.artifact} text={previewText() ?? ""} format={format()} />}>
      {(blob) => <DownloadedReportPreview artifact={props.artifact} blob={blob()} format={format()} />}
    </Show>
    </Show>
    </Show>
  )
}

function DownloadedReportPreview(props: { artifact: DatoxArtifact; blob: Blob; format: string }) {
  const language = useLanguage()
  const [state, setState] = createStore({ text: "", url: "" })

  createEffect(() => {
    if (props.format === "html") {
      const previous = untrack(() => state.url)
      if (previous) URL.revokeObjectURL(previous)
      setState({ text: "", url: URL.createObjectURL(new Blob([props.blob], { type: "text/html;charset=utf-8" })) })
      return
    }
    if (props.format === "pdf") {
      const previous = untrack(() => state.url)
      if (previous) URL.revokeObjectURL(previous)
      void artifactBlobIsPdf(props.blob).then((isPdf) => {
        if (isPdf) {
          setState({ text: "", url: URL.createObjectURL(props.blob) })
          return
        }
        void props.blob.text().then((text) => setState({ text, url: "" }))
      })
      return
    }
    if (props.format === "xlsx" || props.format === "pptx") return
    void props.blob.text().then((text) => setState({ text, url: "" }))
  })

  onCleanup(() => {
    if (state.url) URL.revokeObjectURL(state.url)
  })

  return (
    <Show when={props.format === "html"} fallback={
      <Show when={props.format === "xlsx"} fallback={
        <Show when={props.format === "pptx"} fallback={
          <Show
            when={props.format === "pdf"}
            fallback={<FormatPreview artifact={props.artifact} text={state.text} format={downloadedReportFormat(props.format, state.text)} />}
          >
            <Show when={state.url} fallback={<FormatPreview artifact={props.artifact} text={state.text} format={fallbackReportFormat(state.text)} />}>
              {(src) => <iframe class="h-[720px] w-full rounded-md border border-v2-border-border-base bg-white" src={src()} />}
            </Show>
          </Show>
        }>
          <PptxPreview blob={props.blob} empty={language.t("datox.files.panel.noPreview")} loading={language.t("datox.files.panel.loadingPreview")} slideLabel={(index) => language.t("datox.files.panel.slide", { index })} />
        </Show>
      }>
        <SpreadsheetPreview blob={props.blob} />
      </Show>
    }>
      <Show when={state.url} fallback={<FormatPreview artifact={props.artifact} text={state.text} format={fallbackReportFormat(state.text)} />}>
        {(src) => <ScaledHtmlPreview src={src()} />}
      </Show>
    </Show>
  )
}

function SpreadsheetPreview(props: { blob: Blob }) {
  return <SpreadsheetGridPreview blob={props.blob} />
}

function PdfBlobPreview(props: { blob: Blob; unavailable: string }) {
  const [state, setState] = createStore({ text: "", url: "" })
  createEffect(() => {
    const previous = untrack(() => state.url)
    if (previous) URL.revokeObjectURL(previous)
    void artifactBlobIsPdf(props.blob).then((isPdf) => {
      if (isPdf) {
        setState({ text: "", url: URL.createObjectURL(props.blob) })
        return
      }
      void props.blob.text().then((text) => setState({ text: text || props.unavailable, url: "" }))
    })
  })
  onCleanup(() => {
    const previous = untrack(() => state.url)
    if (previous) URL.revokeObjectURL(previous)
  })
  return (
    <Show when={state.url} fallback={<TextPreview text={state.text || props.unavailable} />}>
      {(src) => <iframe class="h-[720px] w-full rounded-md border border-v2-border-border-base bg-white" src={src()} />}
    </Show>
  )
}

function SpreadsheetGridPreview(props: { blob: Blob }) {
  const language = useLanguage()
  const [state, setState] = createStore({ sheet: "", sheets: [] as string[], rowsBySheet: {} as Record<string, string[][]>, error: "" })
  const rows = createMemo(() => state.rowsBySheet[state.sheet] ?? [])
  const columnCount = createMemo(() => Math.max(1, ...rows().map((row) => row.length)))
  const columns = createMemo(() => Array.from({ length: columnCount() }, (_, index) => spreadsheetColumnName(index)))

  createEffect(() => {
    void normalizedBinaryArtifactBlob(props.blob, "xlsx").then(async (blob) => {
      try {
        const XLSX = await loadXlsxModule()
        const workbook = XLSX.read(await blob.arrayBuffer(), { type: "array", sheetRows: 250 })
        const rowsBySheet = Object.fromEntries(
          workbook.SheetNames.map((sheet) => {
            const rows = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheet], { header: 1, defval: "", blankrows: false }).slice(0, 200)
            return [sheet, trimSpreadsheetRows(rows.map((row) => row.map((cell) => String(cell))))]
          }),
        )
        const previousSheet = untrack(() => state.sheet)
        setState({ sheet: workbook.SheetNames.includes(previousSheet) ? previousSheet : firstVisibleSheet(workbook.SheetNames, rowsBySheet), sheets: workbook.SheetNames, rowsBySheet, error: "" })
      } catch (error) {
        setState("error", error instanceof Error ? error.message : String(error))
      }
    })
  })

  return (
    <div class="overflow-hidden rounded-md border border-v2-border-border-base bg-[#1f1f1f] text-[#f3f3f3]">
      <div class="flex items-center gap-2 border-b border-[#3a3a3a] bg-[#191919] px-3 py-2">
        <div class="rounded bg-[#2a2a2a] px-2 py-1 text-11-medium text-[#d8d8d8]">A1</div>
        <div class="flex min-w-0 flex-1 items-center rounded border border-[#3a3a3a] bg-[#101010] px-2 py-1">
          <span class="mr-2 text-11-medium italic text-[#70ad47]">fx</span>
          <span class="truncate text-11-regular text-[#bdbdbd]">{state.sheet || language.t("datox.pipeline.report")}</span>
        </div>
        <span class="hidden text-11-regular text-[#a6a6a6] sm:inline">{state.sheets.length} {language.t("datox.pipeline.records.tables")} · {Math.min(rows().length, 200)} {language.t("datox.pipeline.output.rows")}</span>
      </div>
      <Show when={!state.error} fallback={<TextPreview text={state.error} />}>
        <Show when={rows().length > 0} fallback={<TextPreview text={language.t("datox.pipeline.report.previewUnavailable")} />}>
          <div class="max-h-[720px] overflow-auto [scrollbar-color:#777_transparent] [scrollbar-width:thin]" style={{ "background-color": "#ffffff" }}>
            <table class="border-separate border-spacing-0 text-left text-12-regular text-[#1f1f1f]">
              <thead>
                <tr>
                  <th class="sticky left-0 top-0 z-30 h-7 min-w-12 border-b border-r border-[#d0d0d0] bg-[#f3f3f3]" />
                  <For each={columns()}>
                    {(column) => <th class="sticky top-0 z-20 h-7 min-w-[140px] border-b border-r border-[#d0d0d0] bg-[#f3f3f3] px-2 text-center text-11-medium text-[#555]">{column}</th>}
                  </For>
                </tr>
              </thead>
              <tbody>
                <For each={rows()}>
                  {(row, rowIndex) => (
                    <tr>
                      <th class="sticky left-0 z-10 h-7 min-w-12 border-b border-r border-[#d0d0d0] bg-[#f3f3f3] px-2 text-right text-11-medium text-[#555]">{rowIndex() + 1}</th>
                      <For each={columns()}>
                        {(_, columnIndex) => <td class="h-7 min-w-[140px] max-w-[360px] whitespace-pre-wrap border-b border-r border-[#d9d9d9] px-2 py-1 align-top" style={{ "background-color": "#ffffff", color: "#1f1f1f" }}>{row[columnIndex()] ?? ""}</td>}
                      </For>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
      </Show>
      <Show when={state.sheets.length > 0}>
        <div class="flex gap-1 overflow-x-auto border-t border-[#3a3a3a] bg-[#191919] px-3 py-2 [scrollbar-color:#777_transparent] [scrollbar-width:thin]">
          <For each={state.sheets}>
            {(sheet) => (
              <button
                type="button"
                class="shrink-0 rounded-t border border-[#3a3a3a] px-3 py-1 text-11-medium"
                classList={{ "border-[#217346] bg-[#217346] text-white": state.sheet === sheet, "bg-[#2a2a2a] text-[#d8d8d8] hover:bg-[#333]": state.sheet !== sheet }}
                onClick={() => setState("sheet", sheet)}
              >
                {sheet}
              </button>
            )}
          </For>
        </div>
      </Show>
    </div>
  )
}

function trimSpreadsheetRows(rows: string[][]) {
  const nonEmptyRows = rows.filter((row) => row.some((cell) => cell.trim()))
  const columnIndexes = Array.from({ length: Math.max(0, ...nonEmptyRows.map((row) => row.length)) }, (_, index) => index).filter((index) =>
    nonEmptyRows.some((row) => (row[index] ?? "").trim()),
  )
  return nonEmptyRows.map((row) => columnIndexes.map((index) => row[index] ?? ""))
}

function firstVisibleSheet(sheets: string[], rowsBySheet: Record<string, string[][]>) {
  return sheets.find((sheet) => rowsBySheet[sheet]?.some((row) => row.some((cell) => cell.trim()))) ?? sheets[0] ?? ""
}

function spreadsheetColumnName(index: number): string {
  const next = Math.floor(index / 26) - 1
  const letter = String.fromCharCode(65 + (index % 26))
  return next >= 0 ? `${spreadsheetColumnName(next)}${letter}` : letter
}

type XlsxModule = {
  read: (data: ArrayBuffer, options: { type: "array"; sheetRows: number }) => { SheetNames: string[]; Sheets: Record<string, unknown> }
  utils: {
    sheet_to_json: <T>(worksheet: unknown, options: { header: 1; defval: string; blankrows: false }) => T[]
  }
}

function loadXlsxModule() {
  const url = "https://esm.sh/xlsx@0.18.5"
  return import(/* @vite-ignore */ url) as Promise<XlsxModule>
}

type JsZipModule = { default: { loadAsync(data: ArrayBuffer): Promise<{ files: Record<string, { dir: boolean; async(type: "string"): Promise<string> }> }> } }
type PptxPreviewModule = { init(container: HTMLElement, options: { width: number; height: number }): { preview(data: ArrayBuffer): Promise<void> | void } }
type PptxSlide = { title: string; lines: string[] }

function loadJsZipModule() {
  const url = "https://esm.sh/jszip@3.10.1"
  return import(/* @vite-ignore */ url) as Promise<JsZipModule>
}

function loadPptxPreviewModule() {
  const url = "https://esm.sh/pptx-preview@1.0.7"
  return import(/* @vite-ignore */ url) as Promise<PptxPreviewModule>
}

async function renderPptx(blob: Blob, container: HTMLDivElement | undefined) {
  if (!container) throw new Error("Missing PPTX preview container")
  container.replaceChildren()
  const module = await loadPptxPreviewModule()
  await module.init(container, { width: 640, height: 360 }).preview(await blob.arrayBuffer())
}

async function extractPptxSlides(blob: Blob): Promise<PptxSlide[]> {
  const JSZip = await loadJsZipModule()
  const archive = await JSZip.default.loadAsync(await blob.arrayBuffer())
  const slidePaths = Object.keys(archive.files)
    .filter((path) => /^ppt\/slides\/slide\d+\.xml$/.test(path))
    .sort((a, b) => Number(a.match(/slide(\d+)\.xml/)?.[1] ?? 0) - Number(b.match(/slide(\d+)\.xml/)?.[1] ?? 0))
  const slides = await Promise.all(slidePaths.map(async (path) => {
    const xml = await archive.files[path].async("string")
    const lines = Array.from(xml.matchAll(/<a:t>([\s\S]*?)<\/a:t>/g)).map((match) => decodeXmlText(match[1]).trim()).filter(Boolean)
    return lines.length > 0 ? { title: lines[0], lines: lines.slice(1) } : undefined
  }))
  return slides.filter((slide): slide is PptxSlide => Boolean(slide))
}

function decodeXmlText(value: string) {
  return value.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&apos;/g, "'")
}

function artifactDownloadFilename(artifact: DatoxArtifact, filename: string) {
  if (filename && filename !== "report") return filename
  const format = artifact.format.toLowerCase()
  return artifact.title.toLowerCase().endsWith(`.${format}`) ? artifact.title : `${artifact.title}.${format}`
}

async function normalizedArtifactDownload(blob: Blob, format: string, filename: string) {
  if (["xlsx", "xlsm"].includes(format.toLowerCase())) return { blob: await normalizedBinaryArtifactBlob(blob, "xlsx") }
  if (!filename.toLowerCase().endsWith(".pdf") || (await artifactBlobIsPdf(blob))) return { blob, filename }
  const text = await blob.text()
  const fallbackFormat = fallbackReportFormat(text)
  return {
    blob: new Blob([text], { type: fallbackFormat === "html" ? "text/html;charset=utf-8" : "text/plain;charset=utf-8" }),
    filename: filename.replace(/\.pdf$/i, `.${fallbackFormat === "html" ? "html" : "txt"}`),
  }
}

async function artifactBlobIsPdf(blob: Blob) {
  const bytes = new Uint8Array(await blob.slice(0, 5).arrayBuffer())
  return bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46 && bytes[4] === 0x2d
}

async function normalizedBinaryArtifactBlob(blob: Blob, format: "xlsx") {
  if (format === "xlsx" && await artifactBlobIsZip(blob)) return blob
  const text = (await blob.text()).trim()
  try {
    const bytes = Uint8Array.from(atob(text), (char) => char.charCodeAt(0))
    if (format === "xlsx" && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04) {
      return new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
    }
  } catch {
    return blob
  }
  return blob
}

async function artifactBlobIsZip(blob: Blob) {
  const bytes = new Uint8Array(await blob.slice(0, 4).arrayBuffer())
  return bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04
}

function fallbackReportFormat(text: string) {
  if (csvRows(text)) return "csv"
  return reportTextLooksLikeHtml(text) ? "html" : "text"
}

function downloadedReportFormat(format: string, text: string) {
  if (["dashboard", "html", "json", "xml", "csv", "text"].includes(format)) {
    return format === "dashboard" ? fallbackReportFormat(text) : format
  }
  return fallbackReportFormat(text)
}

function normalizedReportFormat(format: string) {
  return format.toLowerCase().replace(/^\./, "")
}

function reportTextLooksLikeHtml(text: string) {
  const value = text.trim().toLowerCase()
  return value.startsWith("<") || /<\/?(?:html|body|section|div|table|thead|tbody|tr|td|th|p|h[1-6]|span|style|header|main|article)\b/.test(value)
}

function FormatPreview(props: { artifact: DatoxArtifact; text: string; format: string }) {
  const language = useLanguage()
  if (props.format === "html" && !props.text && props.artifact.preview_html) return <HtmlTextPreview text={props.artifact.preview_html} />
  if (props.format === "json") return <JsonTextPreview text={props.text} />
  if (props.format === "xml") return <XmlTextPreview text={props.text} />
  if (props.format === "csv") return <CsvTextPreview text={props.text} />
  if (props.format === "text") return <TextPreview text={props.text} />
  if (props.format === "pdf") return <TextPreview text={language.t("datox.pipeline.report.pdfLoading")} />
  if (props.format === "html") return <HtmlTextPreview text={props.text} />
  if (props.artifact.preview_html) return <HtmlTextPreview text={props.artifact.preview_html} />
  if (shouldRenderEChartsDashboard(props.artifact)) return <EChartsDashboard charts={echartsCharts(props.artifact)} />
  return (
    <div class="rounded-md border border-v2-border-border-base bg-v2-background-bg-base p-4">
      <p class="text-13-regular text-v2-text-text-muted">{language.t("datox.pipeline.report.previewUnavailable")}</p>
      <JsonPreview data={props.artifact.artifact_metadata} />
    </div>
  )
}

function JsonTextPreview(props: { text: string }) {
  return <TextPreview text={prettyJson(props.text)} />
}

function XmlTextPreview(props: { text: string }) {
  return <TextPreview text={prettyXml(props.text)} />
}

function CsvTextPreview(props: { text: string }) {
  const rows = csvRows(props.text)
  if (rows) return <CsvPreview rows={rows} />
  return <TextPreview text={props.text} />
}

function HtmlTextPreview(props: { text: string }) {
  if (props.text.trim().startsWith("<")) return <ScaledHtmlPreview srcdoc={props.text} />
  return <TextPreview text={props.text} />
}

function ScaledHtmlPreview(props: { src?: string; srcdoc?: string }) {
  const language = useLanguage()
  let frame: HTMLIFrameElement | undefined
  let viewport: HTMLDivElement | undefined
  let documentObserver: ResizeObserver | undefined
  let resizeFrame = 0
  const [state, setState] = createStore({ height: 900, width: 1024, viewportWidth: 0, scale: 1, fit: true })
  const scale = () => state.fit && state.viewportWidth > 0 ? Math.min(1.25, Math.max(0.1, (state.viewportWidth - 32) / state.width)) : state.scale
  const resize = () => {
    const document = frame?.contentDocument
    if (!document) return
    setState("height", Math.max(900, document.body?.scrollHeight ?? 0, document.documentElement?.scrollHeight ?? 0))
  }
  const scheduleResize = () => {
    cancelAnimationFrame(resizeFrame)
    resizeFrame = requestAnimationFrame(resize)
  }
  onMount(() => {
    if (!viewport) return
    const observer = new ResizeObserver(() => setState("viewportWidth", viewport!.clientWidth))
    observer.observe(viewport)
    setState("viewportWidth", viewport.clientWidth)
    onCleanup(() => observer.disconnect())
  })
  onCleanup(() => { documentObserver?.disconnect(); cancelAnimationFrame(resizeFrame) })
  const zoom = (direction: -1 | 1) => setState({ fit: false, scale: Math.min(1.5, Math.max(0.1, Number((scale() + direction * 0.1).toFixed(2)))) })
  return (
    <div class="report-html-preview">
      <div class="report-html-preview__toolbar">
        <span class="report-html-preview__label">{language.t("datox.pipeline.report.documentPreview")}</span>
        <div class="report-html-preview__controls">
          <button type="button" aria-pressed={state.fit} onClick={() => setState("fit", true)}>{language.t("datox.pipeline.report.fitWidth")}</button>
          <button type="button" aria-label={language.t("datox.pipeline.report.actualSize")} aria-pressed={!state.fit && state.scale === 1} onClick={() => setState({ fit: false, scale: 1 })}>100%</button>
          <span class="report-html-preview__divider" aria-hidden="true" />
          <button
            type="button"
            class="report-html-preview__zoom"
            disabled={scale() <= 0.1}
            onClick={() => zoom(-1)}
            aria-label={language.t("datox.pipeline.report.zoomOut")}
          >
            −
          </button>
          <output class="report-html-preview__scale" aria-label={language.t("datox.pipeline.report.previewScale", { scale: `${Math.round(scale() * 100)}%` })}>{Math.round(scale() * 100)}%</output>
          <button
            type="button"
            class="report-html-preview__zoom"
            disabled={scale() >= 1.5}
            onClick={() => zoom(1)}
            aria-label={language.t("datox.pipeline.report.zoomIn")}
          >
            +
          </button>
        </div>
      </div>
      <div ref={viewport} class="report-html-preview__viewport" tabindex="0" role="region" aria-label={language.t("datox.pipeline.report.documentPreview")}>
        <div class="report-html-preview__paper" style={{ width: `${state.width * scale()}px`, height: `${state.height * scale()}px` }}>
          <iframe
            ref={frame}
            title={language.t("datox.pipeline.report.documentPreview")}
            class="report-html-preview__frame"
            style={{ width: `${state.width}px`, height: `${state.height}px`, transform: `scale(${scale()})` }}
            src={props.src}
            srcdoc={props.srcdoc}
            onLoad={() => {
              documentObserver?.disconnect()
              const document = frame?.contentDocument
              if (!document) return
              setState("width", Math.max(1024, document.body?.scrollWidth ?? 0, document.documentElement?.scrollWidth ?? 0))
              documentObserver = new ResizeObserver(scheduleResize)
              if (document.body) documentObserver.observe(document.body)
              documentObserver.observe(document.documentElement)
              scheduleResize()
            }}
          />
        </div>
      </div>
    </div>
  )
}

function reportPreviewText(artifact: DatoxArtifact) {
  const metadata = artifact.artifact_metadata
  const candidates = [metadata.preview_html, metadata.html, metadata.preview_text, metadata.content_preview, metadata.content, metadata.json, metadata.xml, metadata.csv]
  return candidates.find((value): value is string => typeof value === "string" && value.trim().length > 0) ?? ""
}

function previewableReportFormat(format: string) {
  return ["html", "json", "xml", "csv", "pdf", "xlsx", "pptx", "dashboard"].includes(format)
}

function prettyJson(text: string) {
  try {
    return JSON.stringify(JSON.parse(text), null, 2)
  } catch {
    return text || "{}"
  }
}

function prettyXml(text: string) {
  return text.replace(/></g, ">\n<") || "<xml />"
}

function EChartsDashboard(props: { charts: EChartsReportChart[] }) {
  return (
    <div class="grid gap-4 lg:grid-cols-2">
      <For each={props.charts}>
        {(chart) => (
          <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-4">
            <div class="mb-3 flex items-center justify-between gap-3">
              <h4 class="text-13-medium text-v2-text-text-base">{chart.title}</h4>
              <Show when={chart.type}>
                {(type) => <span class="text-12-regular text-v2-text-text-muted">{type()}</span>}
              </Show>
            </div>
            <EChartsCanvas option={chart.option} />
          </section>
        )}
      </For>
    </div>
  )
}

function EChartsCanvas(props: { option: Record<string, unknown> }) {
  let element: HTMLDivElement | undefined
  let chart: { setOption: (option: Record<string, unknown>) => void; resize: () => void; dispose: () => void } | undefined
  let resizeObserver: ResizeObserver | undefined

  createEffect(() => {
    if (!element) return
    void import("echarts").then((echarts) => {
      if (!element) return
      chart?.dispose()
      chart = echarts.init(element, "dark")
      chart.setOption(props.option)
      resizeObserver?.disconnect()
      resizeObserver = new ResizeObserver(() => chart?.resize())
      resizeObserver.observe(element)
    })
  })

  onCleanup(() => {
    resizeObserver?.disconnect()
    chart?.dispose()
  })

  return <div ref={element} class="h-80 w-full" />
}

type EChartsReportChart = {
  title: string
  type?: string
  option: Record<string, unknown>
}

function echartsCharts(artifact: DatoxArtifact): EChartsReportChart[] {
  if (artifact.artifact_metadata.preview_type !== "echarts") return []
  const charts = artifact.artifact_metadata.charts
  if (!Array.isArray(charts)) return []
  return charts.filter(isRecord).flatMap((chart, index) => {
    const option = chart.echarts_option
    if (!isRecord(option)) return []
    return [
      {
        title: typeof chart.title === "string" ? chart.title : `Chart ${index + 1}`,
        type: typeof chart.type === "string" ? chart.type : undefined,
        option,
      },
    ]
  })
}

function shouldRenderEChartsDashboard(artifact: DatoxArtifact) {
  if (artifact.format.toLowerCase() === "html") return false
  return echartsCharts(artifact).length > 0
}

export function ApprovalTab(props: {
  subject?: "report" | "dashboard"
  approvals: DatoxApproval[]
  artifacts: DatoxArtifact[]
  selectedApprovalID?: string
  initialArtifactID?: string
  onReports?: () => void
  onRequest: (artifactID: string, emails: string[], message?: string) => Promise<void>
  onUpdate: (approvalID: string, email: string) => Promise<void>
  onDelete: (approvalID: string) => Promise<void>
}) {
  const language = useLanguage()
  const [store, setStore] = createStore({ artifactID: props.initialArtifactID ?? "", emails: "", message: "", requesting: false, editingID: "", editingEmail: "", updating: "", error: "" as "" | "requestError" | "updateError" | "deleteError" | "copyError", notice: "" as "" | "sent" | "updated" | "removed", filter: "all", query: "", emailTouched: false, copiedID: "" })
  const selectedArtifact = createMemo(() => props.artifacts.find((artifact) => artifact.id === store.artifactID) ?? props.artifacts[0])
  const selectedArtifactID = createMemo(() => selectedArtifact()?.id)
  const reviewers = createMemo(() => props.approvals.filter((approval) => approval.report_artifact_id === selectedArtifactID()))
  const summary = createMemo(() => approvalSummary(reviewers()))
  const recipients = createMemo(() => approvalRecipients(store.emails, reviewers().map((reviewer) => reviewer.approver_email)))
  const editedRecipient = createMemo(() => approvalRecipients(store.editingEmail, reviewers().filter((reviewer) => reviewer.id !== store.editingID).map((reviewer) => reviewer.approver_email)))
  const visible = createMemo(() => reviewers().filter((reviewer) => {
    const kind = approvalStatusKind(reviewer.status)
    return (store.filter === "all" || (store.filter === "pending" ? kind === "pending" || kind === "viewed" : kind === store.filter)) &&
      `${reviewer.approver_email} ${reviewer.comments ?? ""}`.toLowerCase().includes(store.query.trim().toLowerCase())
  }))
  const busy = () => store.requesting || !!store.updating
  let emailInput: HTMLTextAreaElement | undefined
  let mounted = true
  onCleanup(() => { mounted = false })

  createEffect(() => {
    if (props.initialArtifactID) setStore("artifactID", props.initialArtifactID)
  })

  createEffect(() => {
    if (props.artifacts.length && !props.artifacts.some((artifact) => artifact.id === store.artifactID)) setStore("artifactID", props.artifacts[0].id)
  })
  createEffect(() => {
    selectedArtifactID()
    setStore({ editingID: "", editingEmail: "", filter: "all", query: "", error: "", notice: "", copiedID: "" })
  })
  createEffect(() => {
    if (!store.copiedID) return
    const timer = setTimeout(() => setStore("copiedID", ""), 2000)
    onCleanup(() => clearTimeout(timer))
  })

  const submitRequest = async () => {
    if (busy()) return
    const artifact = selectedArtifact()
    const emails = recipients()
    if (!artifact || !emails.emails.length || emails.invalid.length) { setStore("emailTouched", true); return }
    setStore({ requesting: true, error: "", notice: "" })
    try {
      await props.onRequest(artifact.id, emails.emails, store.message.trim() || undefined)
      if (mounted && selectedArtifact()?.id === artifact.id) setStore({ emails: "", message: "", notice: "sent", emailTouched: false, filter: "all", query: "" })
    } catch {
      if (mounted && selectedArtifact()?.id === artifact.id) setStore("error", "requestError")
    } finally {
      if (mounted) setStore("requesting", false)
    }
  }
  const updateReviewer = async (approval: DatoxApproval, remove = false) => {
    if (busy()) return
    const email = editedRecipient()
    if (!remove && (email.emails.length !== 1 || email.invalid.length || email.assigned.length)) return
    setStore({ updating: approval.id, error: "", notice: "" })
    try {
      if (remove) await props.onDelete(approval.id)
      if (!remove) await props.onUpdate(approval.id, email.emails[0])
      if (mounted) setStore({ editingID: "", editingEmail: "", notice: remove ? "removed" : "updated" })
    } catch {
      if (mounted) setStore("error", remove ? "deleteError" : "updateError")
    } finally {
      if (mounted) setStore("updating", "")
    }
  }
  const copyLink = async (approval: DatoxApproval) => {
    try {
      await navigator.clipboard.writeText(new URL(`/review/${encodeURIComponent(approval.id)}`, window.location.origin).href)
      if (mounted) setStore({ copiedID: approval.id, error: "" })
    } catch {
      if (mounted) setStore("error", "copyError")
    }
  }
  return (
    <div class="workflow-approval">
      <header class="approval-heading"><div><h2>{language.t(props.subject === "dashboard" ? "datox.report.dashboardShare.heading" : "datox.pipeline.approval")}</h2><p>{language.t(props.subject === "dashboard" ? "datox.report.dashboardShare.description" : "datox.approval.description")}</p></div>
        <Show when={summary().total} fallback={<span class="approval-status">{language.t("datox.approval.notRequested")}</span>}><ApprovalBadge status={summary().overallStatus} /></Show>
      </header>
      <Show when={props.artifacts.length || props.approvals.length} fallback={<div class="workflow-empty"><IconV2 name="report" size="large" /><h3>{language.t(props.subject === "dashboard" ? "datox.report.dashboardShare.emptyTitle" : "datox.pipeline.workspace.approvalTitle")}</h3><p>{language.t(props.subject === "dashboard" ? "datox.report.dashboardShare.emptyHint" : "datox.pipeline.workspace.approvalHint")}</p><Show when={props.onReports}><button type="button" class="workflow-action" onClick={props.onReports}>{language.t(props.subject === "dashboard" ? "datox.report.dashboardShare.back" : "datox.pipeline.workspace.viewReports")}</button></Show></div>}>
        <Show when={selectedArtifact()}>{(artifact) => <div class="approval-report-picker">
          <label><span>{language.t(props.subject === "dashboard" ? "datox.report.dashboardShare.picker" : "datox.approval.request.report")}</span><select disabled={busy()} value={artifact().id} onChange={(event) => setStore("artifactID", event.currentTarget.value)}><For each={props.artifacts}>{(item) => <option value={item.id}>{reportDisplayTitle(item.title)}</option>}</For></select></label>
          <Show when={htmlReportFormat(artifact().format)}><a class="workflow-action" href={reportPreviewPath(artifact().project_id, artifact().id)} target="_blank" rel="noopener noreferrer">{language.t(props.subject === "dashboard" ? "datox.report.dashboardShare.view" : "datox.approval.viewReport")} ↗</a></Show>
        </div>}</Show>
        <div class="approval-progress" aria-label={language.t("datox.approval.timeline.title")}>
          <ApprovalTimelineItem title={language.t(props.subject === "dashboard" ? "datox.report.dashboardShare.ready" : "datox.approval.timeline.reportReady")} status={selectedArtifact() ? "complete" : "pending"} />
          <ApprovalTimelineItem title={language.t("datox.approval.timeline.reviewersAssigned")} status={summary().total ? "complete" : "pending"} />
          <ApprovalTimelineItem title={language.t("datox.approval.timeline.finalDecision")} status={!summary().total ? "pending" : summary().pending ? "working" : "complete"} />
        </div>
        <Show when={store.error}><p class="approval-notice" data-error role="alert">{language.t(`datox.approval.${store.error || "requestError"}`)}</p></Show>
        <Show when={store.notice}><p class="approval-notice" role="status">{language.t(`datox.approval.${store.notice || "sent"}`)}</p></Show>
        <span class="sr-only" role="status">{store.copiedID ? language.t("datox.approval.linkCopied") : ""}</span>
        <div class="approval-layout">
          <section class="approval-reviewers">
            <div class="approval-section-heading"><h3>{language.t("datox.approval.reviewersTitle")}</h3><span>{language.t(props.subject === "dashboard" ? "datox.report.dashboardShare.scoped" : "datox.approval.reportScoped")}</span></div>
            <div class="approval-metrics">
              <ApprovalMetric label={language.t("datox.approval.metric.reviewers")} value={String(summary().total)} active={store.filter === "all"} onClick={() => setStore("filter", "all")} />
              <ApprovalMetric label={language.t("datox.approval.metric.pending")} value={String(summary().pending)} active={store.filter === "pending"} onClick={() => setStore("filter", "pending")} />
              <ApprovalMetric label={language.t("datox.approval.metric.approved")} value={String(summary().approved)} active={store.filter === "approved"} onClick={() => setStore("filter", "approved")} />
              <ApprovalMetric label={language.t("datox.approval.metric.rejected")} value={String(summary().rejected)} active={store.filter === "rejected"} onClick={() => setStore("filter", "rejected")} />
            </div>
            <Show when={reviewers().length}><input class="approval-search" type="search" aria-label={language.t("datox.approval.search")} placeholder={language.t("datox.approval.search")} value={store.query} onInput={(event) => setStore("query", event.currentTarget.value)} /></Show>
            <Show when={visible().length} fallback={<div class="approval-empty"><IconV2 name="chat" size="large" /><h4>{language.t(reviewers().length ? "datox.pipeline.workspace.noResults" : "datox.approval.noReviewers")}</h4><p>{language.t(reviewers().length ? "datox.approval.noMatches" : props.subject === "dashboard" ? "datox.report.dashboardShare.noReviewersHint" : "datox.approval.noReviewersHint")}</p><Show when={reviewers().length} fallback={<button type="button" class="workflow-action" disabled={!selectedArtifact()} onClick={() => emailInput?.focus()}>{language.t("datox.approval.addReviewers")}</button>}><button type="button" class="workflow-action" onClick={() => setStore({ filter: "all", query: "" })}>{language.t("datox.listing.clear")}</button></Show></div>}>
              <ul class="approval-reviewer-list"><For each={visible()}>{(approval) => <li class="approval-reviewer" data-selected={approval.id === props.selectedApprovalID}>
                <div class="approval-reviewer-heading"><span class="approval-avatar" aria-hidden="true">{approval.approver_email.charAt(0).toUpperCase()}</span><div class="approval-reviewer-identity"><strong>{approval.approver_email}</strong><span>{language.t("datox.approval.updatedAt", { date: formatApprovalDate(approval.updated_at) })}</span></div><ApprovalBadge status={approval.status} /></div>
                <Show when={store.editingID === approval.id} fallback={<div class="approval-reviewer-actions"><button type="button" class="workflow-action" onClick={() => void copyLink(approval)}>{language.t(store.copiedID === approval.id ? "datox.approval.linkCopied" : "datox.approval.copyReviewLink")}</button><button type="button" class="workflow-action" disabled={busy()} onClick={() => setStore({ editingID: approval.id, editingEmail: approval.approver_email, error: "", notice: "" })}>{language.t("common.edit")}</button></div>}>
                  <form class="approval-edit-form" onSubmit={(event) => { event.preventDefault(); void updateReviewer(approval) }}>
                    <label><span>{language.t("datox.approval.table.reviewer")}</span><input type="email" required disabled={busy()} value={store.editingEmail} onInput={(event) => setStore("editingEmail", event.currentTarget.value)} /></label>
                    <Show when={editedRecipient().assigned.length}><p class="approval-field-note">{language.t("datox.approval.alreadyAssigned")}</p></Show>
                    <div class="approval-reviewer-actions"><button type="submit" class="workflow-action" data-primary disabled={busy() || editedRecipient().emails.length !== 1 || !!editedRecipient().invalid.length || !!editedRecipient().assigned.length}>{language.t("common.save")}</button><button type="button" class="workflow-action" disabled={busy()} onClick={() => setStore({ editingID: "", editingEmail: "" })}>{language.t("common.cancel")}</button><button type="button" class="workflow-action approval-remove" disabled={busy()} onClick={() => void updateReviewer(approval, true)}>{language.t("datox.approval.removeReviewer")}</button></div>
                  </form>
                </Show>
                <Show when={approval.comments}><details class="approval-feedback"><summary>{language.t("datox.approval.messageAndFeedback")}</summary><p>{approval.comments}</p></details></Show>
              </li>}</For></ul>
            </Show>
          </section>
          <aside class="approval-request-card">
            <h3>{language.t(props.subject === "dashboard" ? "datox.report.dashboardShare.requestTitle" : "datox.approval.request.title")}</h3><p class="approval-request-description">{language.t(props.subject === "dashboard" ? "datox.report.dashboardShare.requestDescription" : "datox.approval.request.description")}</p>
            <form onSubmit={(event) => { event.preventDefault(); void submitRequest() }}>
              <label><span>{language.t("datox.approval.request.reviewers")}</span><textarea ref={emailInput} rows={3} disabled={busy() || !selectedArtifact()} value={store.emails} placeholder={language.t("datox.approval.request.reviewers.placeholder")} aria-invalid={store.emailTouched && !!recipients().invalid.length} onBlur={() => setStore("emailTouched", true)} onInput={(event) => setStore({ emails: event.currentTarget.value, notice: "", error: "" })} /></label>
              <p class="approval-field-note">{language.t("datox.approval.emailHint")}</p>
              <Show when={store.emailTouched && recipients().invalid.length}><p class="approval-field-note" data-error role="alert">{language.t("datox.approval.invalidEmails", { emails: recipients().invalid.join(", ") })}</p></Show>
              <Show when={recipients().assigned.length}><p class="approval-field-note">{language.t("datox.approval.skippingAssigned")}</p></Show>
              <details class="approval-optional-message"><summary>{language.t("datox.approval.messageOptional")}</summary><label><span class="sr-only">{language.t("datox.approval.request.message")}</span><textarea rows={3} disabled={busy()} value={store.message} placeholder={language.t("datox.approval.request.message.placeholder")} onInput={(event) => setStore("message", event.currentTarget.value)} /></label></details>
              <button type="submit" class="workflow-action approval-send" data-primary disabled={!selectedArtifact() || !recipients().emails.length || !!recipients().invalid.length || busy()} aria-busy={store.requesting}>{language.t(store.requesting ? "datox.approval.request.sending" : "datox.approval.requestReview")}</button>
            </form>
          </aside>
        </div>
      </Show>
    </div>
  )
}

function ApprovalMetric(props: { label: string; value: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" class="approval-metric" aria-pressed={props.active} onClick={props.onClick}><strong>{props.value}</strong><span>{props.label}</span></button>
  )
}

function ApprovalBadge(props: { status: string }) {
  const language = useLanguage()
  const kind = () => approvalStatusKind(props.status)
  return (
    <span
      class="approval-status"
      data-kind={kind()}
    >
      {language.t(`datox.approval.status.${kind()}`)}
    </span>
  )
}

function ApprovalTimelineItem(props: { title: string; status: "complete" | "working" | "pending" }) {
  const language = useLanguage()
  return (
    <div class="approval-step" data-status={props.status}><span class="approval-step-icon" aria-hidden="true"><Show when={props.status === "complete"}><IconV2 name="check" size="small" /></Show></span><div><strong>{props.title}</strong><span>{language.t(`datox.approval.timeline.status.${props.status}`)}</span></div></div>
  )
}

function approvalSummary(approvals: DatoxApproval[]) {
  const approved = approvals.filter((approval) => approvalStatusKind(approval.status) === "approved").length
  const rejected = approvals.filter((approval) => approvalStatusKind(approval.status) === "rejected").length
  const pending = approvals.length - approved - rejected
  return {
    total: approvals.length,
    approved,
    rejected,
    pending,
    overallStatus: rejected ? "rejected" : approvals.length > 0 && pending === 0 ? "approved" : "pending",
  }
}

function approvalStatusKind(status: string) {
  const normalized = status.toLowerCase()
  if (normalized.includes("approve")) return "approved"
  if (normalized.includes("reject") || normalized.includes("decline")) return "rejected"
  if (normalized.includes("view")) return "viewed"
  return "pending"
}

function formatApprovalDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return date.toLocaleString()
}

function JsonPreview(props: { data: Record<string, unknown> }) {
  const rows = tableRows(props.data)
  if (rows) return <CsvPreview rows={rows} />
  return <TextPreview text={JSON.stringify(props.data, null, 2)} />
}

function tableRows(data: Record<string, unknown>) {
  const rows = Array.isArray(data.rows) ? data.rows : Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : undefined
  if (!rows?.length || typeof rows[0] !== "object" || rows[0] === null) return undefined
  const headers = Object.keys(rows[0] as Record<string, unknown>)
  return [headers, ...rows.slice(0, 50).map((row) => headers.map((header) => String((row as Record<string, unknown>)[header] ?? "")))]
}

function TextPreview(props: { text: string }) {
  return (
    <pre class="max-h-[520px] overflow-auto whitespace-pre-wrap rounded-md bg-v2-background-bg-base p-3 text-12-regular text-v2-text-text-base">
      {props.text}
    </pre>
  )
}

function CsvPreview(props: TablePreviewSource) {
  const language = useLanguage()
  const [state, setState] = createStore({ page: 0, pageSize: 100, wrap: false, cell: undefined as { row: number; column: number } | undefined })
  const page = createMemo(() => tablePreviewPage(props, state.page, state.pageSize))
  let viewport: HTMLDivElement | undefined
  createEffect(() => {
    if ("table" in props) props.table
    else props.rows
    setState({ page: 0, cell: undefined })
    if (viewport) viewport.scrollTop = 0
  })
  const selectPage = (index: number) => {
    setState({ page: index, cell: undefined })
    if (viewport) viewport.scrollTop = 0
  }
  return (
    <div data-paged-table data-lesha-table={datoxAgentMode() || undefined} data-wrap={state.wrap}>
    <Show when={datoxAgentMode()}>
      <div class="lesha-table-toolbar"><strong>{language.t("datox.v2.table.details")}</strong><button type="button" aria-pressed={state.wrap} onClick={() => setState("wrap", !state.wrap)}>{language.t("datox.v2.table.wrap")}</button></div>
      <Show when={state.cell}>{(cell) => <div class="lesha-table-cell-detail" role="region" aria-label={language.t("datox.v2.table.value")}>
        <strong>{language.t("datox.v2.table.cell", { row: page().offset + cell().row + 1, column: page().headers[cell().column] })}</strong>
        <button type="button" aria-label={language.t("datox.designer.closePanel")} onClick={() => setState("cell", undefined)}>×</button>
        <pre>{page().rows[cell().row]?.[cell().column] ?? ""}</pre>
      </div>}</Show>
    </Show>
    <div ref={viewport} class="max-h-[560px] overflow-auto rounded-lg border border-v2-border-border-base bg-v2-background-bg-base shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
      <table class="min-w-full border-separate border-spacing-0 text-left text-12-regular text-v2-text-text-base">
        <thead class="sticky top-0 z-10 bg-v2-background-bg-layer-02 text-v2-text-text-muted shadow-[0_1px_0_var(--v2-border-border-base)]">
          <tr>
            <th class="sticky left-0 z-20 w-10 border-r border-v2-border-border-base bg-v2-background-bg-layer-02 px-2 py-2 text-center font-medium text-v2-text-text-faint">
              #
            </th>
            <For each={page().headers}>
              {(cell) => (
                <th class="max-w-[260px] whitespace-nowrap border-r border-v2-border-border-base px-3 py-2 font-medium last:border-r-0">
                  <span class="block truncate">{cell}</span>
                </th>
              )}
            </For>
          </tr>
        </thead>
        <tbody>
          <For each={page().rows}>
            {(row, index) => (
              <tr class="odd:bg-v2-background-bg-layer-01/40 hover:bg-v2-background-bg-layer-02/70">
                <td class="sticky left-0 z-[1] w-10 border-r border-b border-v2-border-border-base bg-inherit px-2 py-2 text-center text-v2-text-text-faint">
                  {page().offset + index() + 1}
                </td>
                <For each={row}>
                  {(cell, column) => (
                    <td class="max-w-[260px] whitespace-pre-wrap border-r border-b border-v2-border-border-base px-3 py-2 align-top leading-5 last:border-r-0">
                      <Show when={datoxAgentMode()} fallback={cell || "-"}>
                        <button type="button" class="lesha-table-cell" aria-label={language.t("datox.v2.table.cell", { row: page().offset + index() + 1, column: page().headers[column()] })} onClick={() => setState("cell", { row: index(), column: column() })}>{cell || "-"}</button>
                      </Show>
                    </td>
                  )}
                </For>
              </tr>
            )}
          </For>
        </tbody>
      </table>
    </div>
    <div class="workflow-table-pagination">
      <span role="status">{language.t("datox.pipeline.table.range", { start: page().start, end: page().end, total: page().total })}</span>
      <div><label>{language.t("datox.pipeline.table.pageSize")}<select value={state.pageSize} onChange={(event) => { setState("pageSize", Number(event.currentTarget.value)); selectPage(0) }}><For each={[50, 100, 250]}>{(size) => <option value={size}>{size}</option>}</For></select></label><button type="button" disabled={page().page === 0} onClick={() => selectPage(page().page - 1)}>{language.t("datox.pipeline.table.previous")}</button><span>{language.t("datox.pipeline.table.page", { current: page().page + 1, total: page().pages })}</span><button type="button" disabled={page().page + 1 >= page().pages} onClick={() => selectPage(page().page + 1)}>{language.t("datox.pipeline.table.next")}</button><input type="number" min="1" max={page().pages} value={page().page + 1} aria-label={language.t("datox.pipeline.table.goToPage")} title={language.t("datox.pipeline.table.goToPage")} onChange={(event) => selectPage(Number.isFinite(event.currentTarget.valueAsNumber) ? event.currentTarget.valueAsNumber - 1 : 0)} /></div>
    </div>
    </div>
  )
}

function normalizedTableRow(row: string[], length: number) {
  return Array.from({ length }, (_, index) => row[index] ?? "")
}

function csvRows(text?: string | null) {
  if (!text?.trim()) return undefined
  const lines = normalizedCsvText(text).split("\n").map((line) => line.trim()).filter(Boolean)
  const headerIndex = lines.findIndex((line) => line.includes(","))
  if (headerIndex < 0) return undefined
  const header = parseCsvLine(lines[headerIndex])
  const width = header?.length ?? 0
  if (width < 2) return undefined
  const rows = lines.slice(headerIndex + 1).filter((line) => line.includes(",")).map((line) => normalizedCsvRow(parseCsvLine(line), width))
  if (rows.length === 0) return undefined
  return [normalizedTableRow(header, width), ...rows]
}

function normalizedCsvRow(row: string[], width: number) {
  if (row.length <= width) return normalizedTableRow(row, width)
  return [...row.slice(0, width - 1), row.slice(width - 1).join(", ")]
}

function normalizedCsvText(text: string) {
  const value = text.trim()
  return value
    .replaceAll("\\r\\n", "\n")
    .replaceAll("\\n", "\n")
    .replaceAll("\\r", "\n")
    .replace(/\r\n?/g, "\n")
}

function sheetRows(part: { part_type: string; preview_text?: string | null }) {
  if (part.part_type !== "sheet") return undefined
  return csvRows(part.preview_text)
}

function parseCsvLine(line: string) {
  const result: string[] = []
  let current = ""
  let quoted = false
  for (const char of line) {
    if (char === '"') {
      quoted = !quoted
      continue
    }
    if (char === "," && !quoted) {
      result.push(current.trim())
      current = ""
      continue
    }
    current += char
  }
  result.push(current.trim())
  return result
}

function FileRow(props: { file: DatoxFile; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      class="workflow-file-row"
      data-active={props.active}
      aria-pressed={props.active}
      title={props.file.filename}
      onClick={props.onClick}
    >
      <FileIcon node={{ path: props.file.filename, type: "file" }} class="size-4 shrink-0" />
      <span class="min-w-0 flex-1 truncate text-13-regular text-v2-text-text-base">{props.file.filename}</span>
    </button>
  )
}

function EmptyList(props: { hasContext: boolean }) {
  const language = useLanguage()
  return <p class="px-2 py-3 text-13-regular text-v2-text-text-muted">{language.t(props.hasContext ? "datox.files.panel.empty" : "datox.files.panel.noContext")}</p>
}

function EmptyPreview() {
  const language = useLanguage()
  return (
    <div class="flex h-full items-center justify-center text-center">
      <div>
        <IconV2 name="workspace" class="mx-auto mb-3 text-v2-icon-icon-muted" />
        <p class="text-14-medium text-v2-text-text-base">{language.t("datox.files.panel.noFileTitle")}</p>
        <p class="mt-2 text-13-regular text-v2-text-text-muted">{language.t("datox.files.panel.noFileDescription")}</p>
      </div>
    </div>
  )
}
